
import { TranslocoService } from '@ngneat/transloco';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  HdChartHoverHighlightService,
  HdChartHoverTarget,
  HdGateKeynoteHoverKind,
} from '../hd-chart-hover-highlight.service';
import { cycleHdBodygraphGateInConfig } from '../hd-bodygraph-config.utils';
import {
  HD_BODYGRAPH_CENTERS,
  HD_BODYGRAPH_CHANNELS,
  HD_BODYGRAPH_GATES,
  HD_BODYGRAPH_GATES_BY_CENTER,
  HD_BODYGRAPH_GATE_TO_CENTER,
  HD_BODYGRAPH_INTEGRATION_GATES,
  getHdBodygraphCornerCapAliases,
  getHdBodygraphCornerCapSegmentAliases,
  getHdBodygraphGateClickableChannelAliases,
  getHdBodygraphGateLayerAliases,
  getHdBodygraphGateSelectedAliases,
  isHdBodygraphThreeCornerCenter,
  isSameHdBodygraphCornerCapCorner,
  resolveHdBodygraphBlackBaseLayerAliases,
  resolveHdBodygraphCornerCapLayerStates,
  resolveHdBodygraphCornerCapOwnerGate,
  resolveHdBodygraphGateAliasColorStateOverrides,
  resolveHdBodygraphGateCandidateAliases,
  resolveHdBodygraphGateBaseLayerAliases,
  resolveHdBodygraphGateLayerAliases,
} from '../hd-bodygraph-metadata';
import {
  HdBodygraphColorConfig,
  HdBodygraphCenterBorderStyle,
  resolveHdBodygraphActiveGateCircleColors,
  resolveHdBodygraphCenterTextColor,
} from '../hd-bodygraph-color-config';
import {
  HdBodygraphCenter,
  HdBodygraphColorState,
  HdBodygraphConfig,
  HdBodygraphGateState,
  HdBodygraphRenderState,
} from '../hd-bodygraph.models';
import { HdGateBlockRect } from '../gate-blocks/hd-gate-block.models';
import { HdBodygraphRenderStateFactory } from '../hd-bodygraph-render-state.factory';
import {
  HdChannelHighlightResolution,
  resolveHdChannelHighlightForGate,
} from '../hd-channel-highlight.utils';
import { HdBodygraphTransitOverlay } from '../hd-bodygraph-transit-overlay.models';
import {
  resolveHdGateTransitVisual,
  resolveHdOverlayLayerVisual,
  toHdOverlaySourceGateState,
  HdGateTransitVisual,
} from '../hd-bodygraph-transit-render.utils';
import {
  HD_JUST_NOW_TRANSIT_BASE_COLOR,
  HD_JUST_NOW_TRANSIT_COLOR,
  HD_JUST_NOW_TRANSIT_TEXT_COLOR,
} from '../../mandala/just-now-transit/hd-just-now-transit';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import {
  HD_MONOPOLE_LAYER_ID,
  HdMonopoleLayerGeometry,
  buildHdMonopoleLayerMarkup,
} from '../hd-bodygraph-monopole-layer';
import {
  HD_LOCKS_KEYS_BADGE_LAYER_CLASS,
  HdBodygraphLocksKeysOverlay,
  HdLocksKeysLayerEllipse,
  HdLocksKeysLayerRect,
  buildHdLocksKeysBadgeLayerMarkup,
  buildHdLocksKeysGateRingMarkup,
  hdLocksKeysKeyBadgeCenter,
  planHdBodygraphLocksKeysOverlay,
} from '../hd-bodygraph-locks-keys-layer';
import { HdCenterMiniCardComponent } from '../basic-properties/hd-center-mini-card.component';
import {
  HdCenterCheatSheetAnchor,
  HdCenterCheatSheetGeometry,
  HdCenterCheatSheetLayerComponent,
} from '../center-cheat-sheet/hd-center-cheat-sheet-layer.component';
import { HD_GATE_PIECE_SHAPE_SELECTOR, HdGatePieceOutlineLayer } from './hd-gate-piece-outline-layer';
import { HdBodygraphSvgService } from './hd-bodygraph-svg.service';
import { HdActivePaletteService } from '../hd-active-palette.service';
import { HD_POST_SHIFT_BROKEN_CHANNEL_KEYS } from '../../hd-global-shift';

/** Vertaalsleutel onder `humanDesign.chart.monopole` voor de hover-tooltip van de laag. */
type HdMonopoleTooltipKey = 'tooltip' | 'designCrystalTooltip' | 'personalityCrystalTooltip';

export type HdBodygraphInteractionMode = 'cycle' | 'reveal-toggle' | 'event-only';

/**
 * Leaf-selector voor het hover-dim mechanisme: alle tekenbare SVG-shapes die
 * een `is-hover-dim` recolor kunnen krijgen. Gedeeld door de statische
 * cache-opbouw (SVG-load) en de dynamische integratie-cache (render-pass).
 */
const HOVER_DIM_LEAF_SELECTOR = 'path, ellipse, circle, rect, polygon, polyline, line, text, tspan';

/** Bronkleuren van de r/b-kanaalsublagen in `bodygraph-full.svg` (klassiek rood en zwart). */
const HD_BODYGRAPH_SVG_DESIGN_SOURCE_FILL = 'rgb(214,0,0)';
const HD_BODYGRAPH_SVG_PERSONALITY_SOURCE_FILL = 'rgb(14,14,14)';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

/**
 * Klasse van de linkerhelft-kloon van een poortcirkel, zie
 * {@link HdBodygraphChartComponent.injectSplitGateCircleHalves}.
 */
const SPLIT_GATE_CIRCLE_HALF_CLASS = 'hd-gate-circle-split-half';

/** Ringgroepen van de Locks & Keys-laag (één per center, boven alle poortcirkels). */
const LOCKS_KEYS_RINGS_CLASS = 'hd-locks-keys-rings';
/** Bovenste laag met de blauwe hover-rand van de gehoverde poort(en). */
const HOVER_GATE_RING_LAYER_CLASS = 'hd-hover-gate-rings';
/** Hover-dim van de Locks & Keys-laag, zie `applyLocksKeysHoverDim`. */
const LOCKS_KEYS_HOVER_DIM_CLASS = 'is-locks-keys-hover-dim';

/**
 * Instantie-teller voor de monopool-laag: de def-ids (gradient/filter/clip)
 * moeten document-breed uniek zijn omdat `url(#…)` niet per SVG scoped is.
 */
let hdMonopoleLayerUidCounter = 0;

/** Zelfde reden als {@link hdMonopoleLayerUidCounter}, voor de clip van de gesplitste poortcirkels. */
let hdSplitGateCircleClipUidCounter = 0;
/** Zelfde reden, voor het masker van de poortstuk-outline ({@link HdGatePieceOutlineLayer}). */
let hdGatePieceOutlineMaskUidCounter = 0;
const TRANSIT_HATCH_PERIOD = 8;
const TRANSIT_HATCH_RUNG_WIDTH = 4;

/**
 * Wat de primaire (LMB) klik op een poort/kanaalstuk doet wanneer er geen
 * spel actief is. Orthogonaal aan {@link HdBodygraphInteractionMode}: tijdens
 * een spel wordt deze input genegeerd en blijft het spel-klikgedrag actief.
 *
 * - `'configure'`: cycle de poort-fill (bestaand edit-gedrag).
 * - `'show-info'`: vraag een info-panel op via {@link gateInfoRequested}.
 */
export type HdGatePrimaryAction = 'configure' | 'show-info';

/**
 * Tijdelijke kanaal-highlight-overlay die door een orchestrator (bv. de
 * Kanalenjacht-game) op de chart kan gezet worden zonder de bestaande
 * `configuration` te muteren. `success` toont het kanaal in de gedeelde
 * highlight-blauw (alsof het defined is), `error` in de design-rood. De
 * timing wordt door de aanroeper beheerd.
 */
export interface HdBodygraphTransientChannelHighlight {
  channelKey: string;
  tone: 'success' | 'error';
}

/**
 * Klik op een kanaalstuk met kanaal-highlight (Info-modus): vraag om het
 * kanaal-info paneel. `channelKeys`/`gates` komen uit de resolve-helper;
 * `anchorGate` is de poort van het aangeklikte kanaalstuk.
 */
export interface HdBodygraphChannelInfoRequest {
  channelKeys: readonly string[];
  gates: readonly number[];
  anchorGate: number;
  origin: { x: number; y: number };
}

interface HdKeynoteTooltipTarget {
  readonly gate: number;
  readonly kind: HdGateKeynoteHoverKind;
}

interface HdKeynoteTooltipRow {
  readonly id: string;
  readonly label: string;
  readonly text: string;
}

interface HdKeynoteTooltipViewModel {
  readonly rows: readonly HdKeynoteTooltipRow[];
  readonly position: { readonly x: number; readonly y: number };
}

interface HdCenterTooltipViewModel {
  readonly center: HdBodygraphCenter;
  readonly variant: 'defined' | 'undefined';
  readonly isOpen: boolean;
  readonly position: { readonly x: number; readonly y: number };
}

@Component({
  selector: 'hd-bodygraph-chart',
  imports: [HdCenterMiniCardComponent, HdCenterCheatSheetLayerComponent],
  templateUrl: './hd-bodygraph-chart.component.html',
  styleUrl: './hd-bodygraph-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdBodygraphChartComponent implements AfterViewInit {
  readonly configuration = input.required<HdBodygraphConfig>();
  private readonly activePalette = inject(HdActivePaletteService);
  /** Kleurenpalet; zonder binding (miniaturen, posters) volgt dit het actieve palet van de gebruiker. */
  readonly colorConfigInput = input<HdBodygraphColorConfig | undefined>(undefined, { alias: 'colorConfig' });
  readonly colorConfig = computed(() => this.colorConfigInput() ?? this.activePalette.colorConfig());
  readonly interactionMode = input<HdBodygraphInteractionMode>('cycle');
  readonly primaryGateAction = input<HdGatePrimaryAction>('configure');
  /**
   * Wanneer `false` worden rechterklik-listeners (`contextmenu`) op
   * gate-elementen niet meer geactiveerd. Hierdoor verschijnt de native
   * browser context-menu (handig om via "Inspect" de DOM te debuggen). De
   * `contextmenu`-event wordt dan niet langer `preventDefault()`ed of
   * `stopPropagation()`d, en `gateContextMenuRequested` wordt niet emit.
   */
  readonly contextMenuEnabled = input(true);
  /**
   * Kanaalhighlight-setting (spec 2026-07-02): hover/klik op een kanaalstuk
   * van een volledig defined kanaal behandelt het hele kanaal als één geheel.
   * Default uit zodat alle andere chart-consumers (games, cheat-sheets)
   * ongewijzigd blijven; de editor-page bindt zijn setting-signal.
   */
  readonly channelHighlightEnabled = input(false);
  /** Toont vertaalde poort- en kanaalkeynotes bij hover op de bodygraph. */
  readonly showHdKeynotes = input(false);
  readonly showCenterCheatSheet = input(false);
  /**
   * Misc-tab → Bodygraph: toont het magnetisch monopool (boven poort 2), de
   * opwaartse licht-spies en de twee bewustzijnskristallen (design in de
   * ajna, personality op het hoofd-puntje) als sier-laag op de chart.
   */
  readonly showMonopoleAndCrystals = input(false);
  /**
   * Locks & keys op de chart buiten de modus (Geavanceerd-paneel, spec
   * 2026-10-10): ringen rond lock- en key-poorten plus slot-/sleutelbadges.
   * `null` = laag uit. In `chart`-modus tellen de activaties van déze
   * bodygraph (`configuration`), zodat elke drieluik-chart zijn eigen kant toont.
   */
  readonly locksKeysOverlay = input<HdBodygraphLocksKeysOverlay | null>(null);
  protected readonly centerCheatSheetGeometry = signal<HdCenterCheatSheetGeometry | null>(null);
  readonly revealedGates = input<ReadonlySet<number>>(new Set<number>());
  readonly errorFlashGates = input<ReadonlySet<number>>(new Set<number>());
  readonly correctFlashGates = input<ReadonlySet<number>>(new Set<number>());
  readonly selectionHighlightGates = input<ReadonlySet<number>>(new Set<number>());
  readonly transientChannelHighlight = input<HdBodygraphTransientChannelHighlight | null>(null);
  readonly transitOverlay = input<HdBodygraphTransitOverlay | null>(null);
  readonly configurationChange = output<HdBodygraphConfig>();
  readonly revealedGatesChange = output<ReadonlySet<number>>();
  readonly gateClicked = output<number>();
  readonly gateInfoRequested = output<{ gate: number; origin: { x: number; y: number } }>();
  readonly channelInfoRequested = output<HdBodygraphChannelInfoRequest>();
  readonly centerInfoRequested = output<{
    center: HdBodygraphCenter;
    origin: { x: number; y: number };
  }>();
  readonly gateContextMenuRequested = output<{
    gate: number;
    position: { x: number; y: number };
    /** Aanwezig wanneer de rechterklik op een kanaalstuk met kanaal-highlight viel (spec §3.5). */
    channel?: { channelKeys: readonly string[]; gates: readonly number[] };
  }>();
  readonly centerContextMenuRequested = output<{ center: HdBodygraphCenter; position: { x: number; y: number } }>();
  /** Klik op het monopool-icoon of een bewustzijnskristal (Misc-laag): opent het paneel "De drie dragers". */
  readonly monopoleInfoRequested = output<{ origin: { x: number; y: number } }>();
  /**
   * Emit telkens de laad-status van de bodygraph-SVG wijzigt: `true` wanneer
   * de SVG niet geladen kon worden, `false` bij een geslaagde load. Hiermee
   * kan de pagina de volledige chart-zone (incl. planeetgrids) verbergen en
   * een nette foutmelding tonen i.p.v. een gebroken layout.
   */
  readonly loadErrorChange = output<boolean>();

  protected readonly loading = signal(true);
  protected readonly loadError = signal<string | null>(null);

  /**
   * Poort-ankers voor overlays (planeetblokjes-laag): per poort de union-bbox
   * van de geïndexeerde poortnummer-elementen, omgerekend naar viewBox-
   * coördinaten. `null` tot de SVG geladen is. Statisch t.o.v. de viewBox —
   * geen recompute bij resize/zoom nodig (spec 2026-07-05 §5).
   */
  readonly gateAnchors = signal<ReadonlyMap<number, HdGateBlockRect> | null>(null);

  private readonly bodygraphSvg = inject(HdBodygraphSvgService);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });
  private readonly svgHost = viewChild.required<ElementRef<HTMLDivElement>>('svgHost');

  readonly gateHovered = output<number | null>();
  readonly centerHovered = output<HdBodygraphCenter | null>();

  private readonly indexedElementsById = new Map<string, SVGElement[]>();
  private readonly indexedElementsByAlias = new Map<string, SVGElement[]>();
  private readonly originalFillValues = new WeakMap<SVGElement, string | null>();
  /** Shapes waarvan de fill tijdelijk door een transitoverlay is overschreven. */
  private readonly transitPaintedShapes = new Set<SVGElement>();
  private readonly transitHatchPatternIds = new Map<string, string>();
  private transitHatchPatternSequence = 0;
  private readonly listenerCleanups: Array<() => void> = [];
  /**
   * Unsubscribe van de synchrone hover-`onStateChange`-listener. Bewust NIET
   * in {@link listenerCleanups}: die array wordt door {@link bindGateInteractions}
   * (via {@link cleanupListeners}) bij elke SVG-(re)load geleegd. De
   * hover-listener wordt éénmalig in de constructor geregistreerd en mag dus
   * enkel bij `destroy` opgeruimd worden — anders stopt de chart na de eerste
   * SVG-load met dimmen.
   */
  private hoverStateUnsubscribe: (() => void) | null = null;
  private readonly gateNumberTargetsByGate = new Map<number, Element[]>();
  private readonly gateQuestionMarkTargetsByGate = new Map<number, SVGTextElement[]>();
  /** Linkerhelft-klonen per poort, zie {@link injectSplitGateCircleHalves}. */
  private readonly splitGateCircleHalvesByGate = new Map<number, SVGElement[]>();
  private readonly splitGateCircleClipId = `hd-gate-circle-split-clip-${++hdSplitGateCircleClipUidCounter}`;

  /**
   * Hover-highlight caches — eenmalig opgebouwd na het laden van de SVG
   * (zie {@link buildHoverHighlightCaches}). Het hover-pad gebruikt enkel
   * deze caches en doet geen DOM-traversals (`querySelectorAll`/`closest`)
   * meer per hover-event, zodat de chart synchroon meebeweegt met de
   * Signal-gestuurde planet grid.
   */
  private readonly hoverDimLeaves: Element[] = [];
  private readonly hoverKeepLeavesByCenter = new Map<HdBodygraphCenter, Set<Element>>();
  private readonly hoverKeepLeavesByGate = new Map<number, Set<Element>>();
  /**
   * Dynamische aanvulling op {@link hoverKeepLeavesByGate} voor de
   * integratiepoorten (10/20/34/57). De render wijst de gedeelde bruglagen
   * (c5710/c5720/c5734) en corner-caps per `activeGates`-combinatie aan een
   * poort toe (ownership — {@link resolveHdBodygraphGateCandidateAliases});
   * de statische keep-sets kennen die toewijzing niet. Deze cache wordt
   * daarom bij elke {@link applyRenderState} herbouwd met dezelfde
   * ownership-logica, zodat bv. een hover op kanaal 20-34 ook het brugstuk
   * helder houdt. Het hover-pad zelf blijft lookup-only.
   */
  private readonly hoverIntegrationKeepLeavesByGate = new Map<number, Set<Element>>();
  private readonly hoverSourceCenterMarkersByCenter = new Map<HdBodygraphCenter, Element[]>();
  private readonly hoverSourceGateMarkersByGate = new Map<number, Element[]>();
  /**
   * Vlakvormen van het kanaalstuk per poort (kanaalgroep + segmentlagen, zonder
   * poortcirkel): het silhouet voor "poortstukken highlighten". Wat er
   * zichtbaar van is, en de integratie-aanvulling uit
   * {@link hoverIntegrationKeepLeavesByGate}, bepaalt de outline.
   */
  private readonly hoverPieceLeavesByGate = new Map<number, Element[]>();
  /**
   * Outline-eigenaarschap van de integratiepoorten (10/20/34/57): de lagen die
   * de render in de huidige configuratie aan de poort toewijst (kandidaat-
   * lagen) plus enkel de corner-caps waarvan hij eigenaar is. Vervangt voor
   * deze poorten {@link hoverPieceLeavesByGate} (voor 57 zijn dat statisch alle
   * drie de lange bruglagen) en is strenger dan
   * {@link hoverIntegrationKeepLeavesByGate}, waar elke actieve integratiepoort
   * alle caps helder houdt. Herbouwd per render-pass, net als die keep-cache.
   */
  private readonly outlineIntegrationLeavesByGate = new Map<number, Element[]>();
  /** Alle vlakvormen van het integratiegebied: wat over een eigen stuk kan liggen. */
  private readonly integrationClusterLeaves: Element[] = [];
  private readonly activeDimmedLeaves = new Set<Element>();
  private readonly activeSourceMarkers = new Set<Element>();
  /**
   * Onthoudt de keep-union van de vorige hover-state zodat
   * {@link applyHoverDimDelta} bij een hover-transitie enkel de symmetrische
   * verschil-set hoeft aan te raken (i.p.v. alle {@link hoverDimLeaves}).
   * `null` = geen actieve hover (volgende activering doet één full-pass).
   */
  private currentHoverKeepUnion: Set<Element> | null = null;
  /**
   * Cached SVG-root na de SVG-load, zodat het hover-pad geen
   * `querySelector('svg')` per event meer hoeft te doen.
   */
  private svgRoot: SVGSVGElement | null = null;
  private svgResizeObserver: ResizeObserver | null = null;

  private svgLoaded = false;
  private readonly monopoleLayerUid = ++hdMonopoleLayerUidCounter;
  /** Ringgroepen + badges van de Locks & Keys-laag (met `data-center`) voor de hover-dim. */
  private readonly locksKeysHoverTargets: Element[] = [];
  /** Gemeten poortcirkels (viewBox) — statisch t.o.v. de viewBox, dus eenmalig per SVG-load. */
  private readonly gateCircleGeometryByGate = new Map<number, HdLocksKeysLayerEllipse>();
  /** Zie {@link applyHoverGateRings}; lazy aangemaakt bij de eerste poort-hover. */
  private hoverGateRingLayer: SVGGElement | null = null;
  /** Zie {@link applyHoverGatePieceOutlines}; lazy aangemaakt per SVG-load. */
  private gatePieceOutlineLayer: HdGatePieceOutlineLayer | null = null;
  private readonly gatePieceOutlineMaskId = `hd-hover-gate-piece-mask-${++hdGatePieceOutlineMaskUidCounter}`;
  /** Muispositie boven het monopool-icoon of een kristal (+ welke tekst); null = geen tooltip. */
  private readonly monopoleTooltipPosition = signal<{ x: number; y: number; key: HdMonopoleTooltipKey } | null>(null);
  private readonly hdKeynoteTooltipPosition = signal({ x: 0, y: 0 });
  private resolvedGateInfoService: HdGateInfoService | null = null;
  private resolvedContentLanguageService: HdContentLanguageService | null = null;
  private resolvedTranslocoService: TranslocoService | null = null;
  private readonly hdKeynoteActivatedGates = computed<ReadonlySet<number>>(() => new Set([
    ...this.configuration().activations.map(activation => activation.gate),
    ...(this.transitOverlay()?.activations.map(activation => activation.gate) ?? []),
  ]));

  /** Poorten die déze bodygraph activeert (voor de `chart`-modus van de L&K-laag). */
  private readonly locksKeysOverlayActivatedGates = computed<ReadonlySet<number>>(() =>
    new Set(this.configuration().activations.map(activation => activation.gate)));

  private readonly activeHdKeynoteTooltip = computed<{
    readonly target: HdKeynoteTooltipTarget;
    readonly position: { readonly x: number; readonly y: number };
  } | null>(() => {
    const keynoteHover = this.hoverService?.keynoteHover();
    if (keynoteHover == null || keynoteHover.kind === 'center') return null;

    if (keynoteHover.source === 'chart-gate') {
      return {
        target: { gate: keynoteHover.gate, kind: keynoteHover.kind },
        position: this.hdKeynoteTooltipPosition(),
      };
    }

    if (this.hoverService?.keynotesAtHoverOnly() ?? true) return null;
    const position = this.resolveGateKeynotePosition(keynoteHover.gate);
    if (position == null) return null;
    return {
      target: { gate: keynoteHover.gate, kind: 'gate' },
      position,
    };
  });

  /**
   * Tooltip op het monopool-icoon (Misc-tab → Bodygraph): benoemt de zetel
   * van het monopool (poort 2), want het icoon zelf staat in het midden van
   * het G-center zodat het geen poortnummers bedekt.
   */
  protected readonly monopoleTooltip = computed<{ text: string; position: { x: number; y: number } } | null>(() => {
    const position = this.monopoleTooltipPosition();
    if (position == null) {
      return null;
    }
    const language = this.contentLanguageService.effectiveLang();
    return {
      text: this.translocoService.translate(`humanDesign.chart.monopole.${position.key}`, {}, language),
      position,
    };
  });

  protected readonly hdKeynoteTooltip = computed<HdKeynoteTooltipViewModel | null>(() => {
    if (!this.showHdKeynotes()) {
      return null;
    }

    const activeTooltip = this.activeHdKeynoteTooltip();
    if (activeTooltip == null) return null;

    const rows = this.buildHdKeynoteTooltipRows(activeTooltip.target);
    if (rows.length === 0) {
      return null;
    }

    return {
      rows,
      position: activeTooltip.position,
    };
  });

  protected readonly baseRenderState = computed(() => this.renderStateFactory.create(this.configuration()));

  protected readonly hdCenterTooltip = computed<HdCenterTooltipViewModel | null>(() => {
    if (!this.showHdKeynotes()) return null;

    const keynoteHover = this.hoverService?.keynoteHover();
    if (keynoteHover?.kind !== 'center') return null;

    const centerState = this.baseRenderState().centerStates[keynoteHover.center];
    const position = this.resolveCenterKeynotePosition(keynoteHover.center, centerState.isDefined);
    if (position == null) return null;

    const centerGates = HD_BODYGRAPH_GATES_BY_CENTER[keynoteHover.center];
    const isOpen = !this.configuration().activations.some(activation =>
      centerGates.includes(activation.gate),
    );

    return {
      center: keynoteHover.center,
      variant: centerState.isDefined ? 'defined' : 'undefined',
      isOpen,
      position,
    };
  });

  private readonly renderState = computed(() => {
    const transitOverlay = this.transitOverlay();
    const configuration = transitOverlay == null
      ? this.configuration()
      : {
          ...this.configuration(),
          activations: [
            ...this.configuration().activations,
            ...transitOverlay.activations.map(activation => ({
              ...activation,
              id: `just-now-transit:${activation.id}`,
            })),
          ],
        };
    const combinedState = this.renderStateFactory.create(configuration);
    const withChannelOverlay = this.applyTransientChannelHighlightOverlay(combinedState, this.transientChannelHighlight());
    return this.applyCorrectFlashGatesOverlay(withChannelOverlay, this.correctFlashGates());
  });

  /**
   * Poorten van het actieve transient channel highlight. Worden meegerekend
   * bij `applyGateNumberVisibility` zodat de speler in de Kanalenjacht-
   * memory-modus de poortnummers van het gehighlighte kanaal kan zien
   * zonder dat de algemene `'event-only'`-mode die nummers verbergt.
   */
  private readonly transientChannelHighlightGates = computed<ReadonlySet<number>>(() => {
    const overlay = this.transientChannelHighlight();
    if (overlay == null) {
      return new Set<number>();
    }
    const channel = HD_BODYGRAPH_CHANNELS.find(c => c.key === overlay.channelKey);
    return channel ? new Set<number>(channel.gates) : new Set<number>();
  });

  constructor() {
    effect(() => {
      const renderState = this.renderState();
      const baseRenderState = this.baseRenderState();
      const transitOverlay = this.transitOverlay();
      // `colorConfig` wordt pas dieper in `applyRenderState` gelezen (na de
      // svgLoaded-guard). Draaide de eerste effect-run vóór het laden van de SVG
      // (early-return), dan zat colorConfig niet in de dependency-set en werden
      // losse kleurwijzigingen (bv. "kleur actieve poorten") niet realtime
      // toegepast — enkel bij een reload/nieuwe chart. Lees hem daarom hier,
      // vóór de guard, zodat elke kleurwijziging de render altijd hertriggert.
      this.colorConfig();

      if (!this.svgLoaded) {
        return;
      }

      this.applyRenderState(renderState, baseRenderState, transitOverlay);
    });

    effect(() => {
      const interactionMode = this.interactionMode();
      const revealedGates = this.revealedGates();
      const errorFlashGates = this.errorFlashGates();
      const correctFlashGates = this.correctFlashGates();
      const selectionHighlightGates = this.selectionHighlightGates();

      if (!this.svgLoaded) {
        return;
      }

      this.applyGateNumberVisibility(interactionMode, revealedGates, errorFlashGates, correctFlashGates, selectionHighlightGates);
      this.applyErrorFlash(errorFlashGates);
      this.applyCorrectFlash(correctFlashGates);
      this.applySelectionHighlight(selectionHighlightGates);
    });

    // Monopool & bewustzijnskristallen: lees de input vóór de svgLoaded-guard
    // zodat hij in de dependency-set zit wanneer de eerste run nog vóór de
    // SVG-load valt (zelfde les als colorConfig hierboven).
    effect(() => {
      const showMonopoleAndCrystals = this.showMonopoleAndCrystals();

      if (!this.svgLoaded) {
        return;
      }

      this.applyMonopoleLayer(showMonopoleAndCrystals);
    });

    // Locks & Keys-laag: zelfde les, alle inputs vóór de svgLoaded-guard lezen.
    // Het monopool telt mee omdat het de slot-badge in het G-center verdringt.
    effect(() => {
      const overlay = this.locksKeysOverlay();
      const activatedGates = this.locksKeysOverlayActivatedGates();
      const monopoleVisible = this.showMonopoleAndCrystals();

      if (!this.svgLoaded) {
        return;
      }

      this.applyLocksKeysLayer(overlay, activatedGates, monopoleVisible);
    });

    effect(() => {
      const renderState = this.renderState();
      const loading = this.loading();
      if (loading || !this.svgLoaded || !this.hasCenterShapeTargets()) return;
      this.syncCenterAccessibility(renderState, this.centerAccessibilityLanguage());
    });

    // Hover-dimming loopt via een SYNCHRONE listener i.p.v. een `effect()`.
    // Reden: het effect draait in een microtask, ná de signal-write maar
    // vóór/náast de CD-tick die planet-grid en mandala via template-bindings
    // updaten — dat gaf de "2-staps dim"-regressie (chart één frame later).
    // Door op `onStateChange` te draaien landt de chart-classList-mutatie in
    // dezelfde call-stack als de signal-write, dus dezelfde frame als grid +
    // mandala. Zie `docs/human-design/hd-chart-hover-2-step-regressie-analyse.md` §7.
    const hs = this.hoverService;
    if (hs) {
      this.hoverStateUnsubscribe = hs.onStateChange(() => this.applyHoverFromService());
    }

    this.destroyRef.onDestroy(() => {
      this.cleanupListeners();
      this.svgResizeObserver?.disconnect();
      this.svgResizeObserver = null;
      this.hoverStateUnsubscribe?.();
      this.hoverStateUnsubscribe = null;
    });
  }

  async ngAfterViewInit(): Promise<void> {
    await this.loadSvg();
  }

  private async loadSvg(): Promise<void> {
    try {
      const markup = await this.bodygraphSvg.load();

      const host = this.svgHost().nativeElement;
      host.innerHTML = markup;

      const svg = host.querySelector('svg');

      if (!svg) {
        throw new Error('De bodygraph SVG kon niet geladen worden.');
      }

      this.svgRoot = svg as SVGSVGElement;
      this.indexSvgElements(svg);
      this.gateCircleGeometryByGate.clear();
      this.hoverGateRingLayer = null;
      this.gatePieceOutlineLayer = null;
      this.indexGateNumberTargets(svg);
      // Vóór de hover-caches: de klonen moeten als gewone leaves in de
      // keep/dim-sets van hun poort en center terechtkomen.
      this.injectSplitGateCircleHalves(svg);
      this.buildHoverHighlightCaches(svg);
      this.bindGateInteractions(svg);
      this.svgLoaded = true;
      if (this.hasCenterShapeTargets()) {
        this.syncCenterAccessibility(
          this.renderState(),
          this.centerAccessibilityLanguage(),
        );
      }
      this.observeSvgLayout(svg as SVGSVGElement);
      this.loading.set(false);
      this.loadError.set(null);
      this.loadErrorChange.emit(false);
      this.applyRenderState(this.renderState(), this.baseRenderState(), this.transitOverlay());
      this.gateAnchors.set(this.computeGateAnchors());
      this.applyMonopoleLayer(this.showMonopoleAndCrystals());
      this.applyLocksKeysLayer(
        this.locksKeysOverlay(),
        this.locksKeysOverlayActivatedGates(),
        this.showMonopoleAndCrystals(),
      );
      requestAnimationFrame(() => this.refreshCenterCheatSheetGeometry());
      this.applyGateNumberVisibility(
        this.interactionMode(),
        this.revealedGates(),
        this.errorFlashGates(),
        this.correctFlashGates(),
        this.selectionHighlightGates(),
      );
      this.applyErrorFlash(this.errorFlashGates());
      this.applyCorrectFlash(this.correctFlashGates());
      this.applySelectionHighlight(this.selectionHighlightGates());
      // De hover-`effect()` is vervangen door een synchrone listener (zie
      // constructor); die vuurt niet bij SVG-load. Pas dus een reeds actieve
      // (bevroren / initiële) hover-state alsnog eenmalig toe.
      this.applyHoverFromService();
    } catch {
      this.loadError.set('De bodygraph SVG kon niet geladen worden.');
      this.loading.set(false);
      this.loadErrorChange.emit(true);
    }
  }

  /**
   * De transit-arcering compenseert de actuele SVG-CTM. Een moduswissel kan de
   * bodygraph na de eerste render nog herschalen; bouw de paint servers dan
   * opnieuw op met de definitieve layout in plaats van op een hover-repaint te
   * vertrouwen.
   */
  private observeSvgLayout(svg: SVGSVGElement): void {
    this.svgResizeObserver?.disconnect();
    this.svgResizeObserver = new ResizeObserver(() => {
      this.refreshCenterCheatSheetGeometry();
      // Schaal (px per viewBox-eenheid) van de poortstuk-outline opnieuw meten.
      this.gatePieceOutlineLayer?.invalidateMetrics();
      if (!this.svgLoaded || this.transitOverlay() == null) {
        return;
      }
      this.applyRenderState(this.renderState(), this.baseRenderState(), this.transitOverlay());
    });
    this.svgResizeObserver.observe(svg);
  }

  private refreshCenterCheatSheetGeometry(): void {
    if (!this.svgLoaded) return;
    const stageRect = this.svgHost().nativeElement.getBoundingClientRect();
    if (stageRect.width <= 0 || stageRect.height <= 0) return;

    const anchors: Partial<Record<HdBodygraphCenter, HdCenterCheatSheetAnchor>> = {};
    HD_BODYGRAPH_CENTERS.forEach(center => {
      const rects = (this.hoverSourceCenterMarkersByCenter.get(center) ?? [])
        .map(element => element.getBoundingClientRect())
        .filter(rect => rect.width > 0 && rect.height > 0);
      if (rects.length === 0) return;
      const left = Math.min(...rects.map(rect => rect.left)) - stageRect.left;
      const right = Math.max(...rects.map(rect => rect.right)) - stageRect.left;
      const top = Math.min(...rects.map(rect => rect.top)) - stageRect.top;
      const bottom = Math.max(...rects.map(rect => rect.bottom)) - stageRect.top;
      anchors[center] = { left, right, centerY: (top + bottom) / 2 };
    });
    if (Object.keys(anchors).length !== HD_BODYGRAPH_CENTERS.length) return;
    this.centerCheatSheetGeometry.set({ anchors });
  }

  private indexSvgElements(root: Element): void {
    this.indexedElementsById.clear();
    this.indexedElementsByAlias.clear();

    root.querySelectorAll('*').forEach(element => {
      if (!(element instanceof SVGElement)) {
        return;
      }

      this.cacheOriginalFill(element);

      const elementId = element.getAttribute('id')?.trim();
      const serifId = element.getAttribute('serif:id')?.trim();

      if (elementId) {
        this.indexElement(this.indexedElementsById, elementId, element);
      }

      if (serifId) {
        this.indexElement(this.indexedElementsByAlias, serifId, element);
      }
    });
  }

  private indexElement(index: Map<string, SVGElement[]>, alias: string, element: SVGElement): void {
    const existingElements = index.get(alias) ?? [];

    if (!existingElements.includes(element)) {
      existingElements.push(element);
      index.set(alias, existingElements);
    }
  }

  private bindGateInteractions(root: Element): void {
    this.cleanupListeners();

    for (const gate of HD_BODYGRAPH_GATES) {
      const gateNumberTargets = this.findGateNumberTargets(root, gate);
      const circleTargets = this.findElementsByAliases(getHdBodygraphGateSelectedAliases(gate));
      const channelTargets = this.findGateChannelTargets(gate);

      // Poort-elementen (gate-cijfer + cirkel): bewust altijd éénpoorts-gedrag —
      // dit is de "alleen deze poort"-route uit de kanaalhighlight-spec (§3.2).
      const gateOnlyTargets = new Set<Element>([...gateNumberTargets, ...circleTargets]);
      gateOnlyTargets.forEach(target => {
        this.bindClickListener(target, gate);
        this.bindGateHoverListener(target, gate);
      });

      // Kanaal-elementen: channel-aware gedrag met fallback op het oude gedrag.
      channelTargets.forEach(target => {
        if (gateOnlyTargets.has(target)) return;
        this.bindChannelAwareClickListener(target, gate);
        this.bindChannelAwareHoverListener(target, gate);
      });

      const relationTargets = new Set<Element>([...circleTargets, ...channelTargets]);
      relationTargets.forEach(target => this.bindRelatedHoverListeners(target, gateNumberTargets));
    }

    this.bindCenterHoverListeners();
  }

  /**
   * Hover-listener op alle klikbare elementen van een poort (gate-cijfer,
   * cirkel, kanaalstuk, segment-aliassen). Stuurt het hover-event door naar
   * de {@link HdChartHoverHighlightService} (indien geprovide) en stopt de
   * propagation zodat center-hover er niet op afgaat. Zie
   * `docs/human-design/hd-chart-hover-highlight.md`.
   */
  private bindGateHoverListener(target: Element, gate: number): void {
    const enter = (event: Event) => {
      event.stopPropagation();
      this.hoverService?.setKeynoteHover(gate, 'chart-gate', 'gate');
      this.hoverService?.setGateHover(gate, 'chart-gate');
      this.gateHovered.emit(gate);
    };
    const leave = () => {
      this.hoverService?.setKeynoteHover(null, 'chart-gate');
      this.hoverService?.setGateHover(null, 'chart-gate');
      this.gateHovered.emit(null);
    };
    target.addEventListener('mouseenter', enter);
    target.addEventListener('mouseleave', leave);
    this.listenerCleanups.push(() => {
      target.removeEventListener('mouseenter', enter);
      target.removeEventListener('mouseleave', leave);
    });
  }

  /**
   * Kanaal-highlight resolutie voor een kanaalstuk (spec §3.1): alle defined
   * kanalen die deze poort bevatten, of `null` wanneer de setting uit staat of
   * er geen definitie is — de caller valt dan terug op het poort-gedrag.
   */
  private resolveChannelHighlight(gate: number): HdChannelHighlightResolution | null {
    if (!this.channelHighlightEnabled()) return null;
    return resolveHdChannelHighlightForGate(this.renderState().channelStates, gate);
  }

  /**
   * Hover-listener voor kanaalstuk-elementen: highlight het hele defined
   * kanaal (gates-hover) of val terug op de éénpoorts-hover. De leave loopt
   * via dezelfde unconditional clear als de gewone gate-hover.
   */
  private bindChannelAwareHoverListener(target: Element, gate: number): void {
    const enter = (event: Event) => {
      event.stopPropagation();
      const channelGates = computed<ReadonlySet<number>>(() =>
        new Set(this.resolveActivatedKeynoteChannelGates(gate)),
      );
      this.hoverService?.setKeynoteHover(
        gate,
        'chart-gate',
        'channel',
        channelGates,
      );
      const resolution = this.resolveChannelHighlight(gate);
      if (resolution) {
        this.hoverService?.setGatesHover(resolution.gates, 'chart-gate');
      } else {
        this.hoverService?.setGateHover(gate, 'chart-gate');
      }
      this.gateHovered.emit(gate);
    };
    const leave = () => {
      this.hoverService?.setKeynoteHover(null, 'chart-gate');
      this.hoverService?.setGateHover(null, 'chart-gate');
      this.gateHovered.emit(null);
    };
    target.addEventListener('mouseenter', enter);
    target.addEventListener('mouseleave', leave);
    this.listenerCleanups.push(() => {
      target.removeEventListener('mouseenter', enter);
      target.removeEventListener('mouseleave', leave);
    });
  }

  /**
   * Click/contextmenu-listener voor kanaalstuk-elementen. Verschillen met
   * {@link bindClickListener}:
   *  - Ctrl/Cmd+klik met kanaal-highlight bevriest/ontdooit de hele poorten-set;
   *  - LMB in Info-modus met kanaal-highlight emit `channelInfoRequested`;
   *  - rechterklik geeft het kanaal-payload mee aan `gateContextMenuRequested`.
   * Zonder kanaal-highlight (setting uit / geen definitie) is het gedrag
   * identiek aan de poort-elementen.
   */
  private bindChannelAwareClickListener(target: Element, gate: number): void {
    const clickListener = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      const mouseEvent = event as MouseEvent;
      const resolution = this.resolveChannelHighlight(gate);
      if (mouseEvent.ctrlKey || mouseEvent.metaKey) {
        if (resolution && this.hoverService?.enabled()) {
          this.hoverService.toggleFreezeForGates(resolution.gates);
          return;
        }
        if (this.tryHandleFreezeToggleShortcut(mouseEvent, { kind: 'gate', gate })) {
          return;
        }
      }
      this.handleChannelAwareGateClick(gate, resolution, mouseEvent);
    };
    const contextMenuListener = (event: Event) => {
      if (!this.contextMenuEnabled()) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.handleChannelAwareGateContextMenu(gate, event as MouseEvent);
    };

    target.addEventListener('click', clickListener);
    target.addEventListener('contextmenu', contextMenuListener);
    target.classList.add('is-clickable');

    this.listenerCleanups.push(() => {
      target.removeEventListener('click', clickListener);
      target.removeEventListener('contextmenu', contextMenuListener);
      target.classList.remove('is-clickable');
    });
  }

  private handleChannelAwareGateClick(
    gate: number,
    resolution: HdChannelHighlightResolution | null,
    event: MouseEvent,
  ): void {
    const mode = this.interactionMode();

    this.gateClicked.emit(gate);

    if (mode === 'reveal-toggle') {
      this.emitToggledRevealedGate(gate);
      return;
    }

    if (mode !== 'cycle') {
      return;
    }

    if (this.primaryGateAction() === 'show-info') {
      if (resolution) {
        this.channelInfoRequested.emit({
          channelKeys: resolution.channelKeys,
          gates: resolution.gates,
          anchorGate: gate,
          origin: { x: event.clientX, y: event.clientY },
        });
        return;
      }
      this.gateInfoRequested.emit({ gate, origin: { x: event.clientX, y: event.clientY } });
      return;
    }

    this.configurationChange.emit(cycleHdBodygraphGateInConfig(this.configuration(), gate));
  }

  private handleChannelAwareGateContextMenu(gate: number, event: MouseEvent): void {
    if (this.interactionMode() !== 'cycle') {
      return;
    }
    // Zelfde suspend-reden als handleGateContextMenu: het CDK-overlay van het
    // menu triggert anders een mouseleave die de (kanaal)hover-state cleart.
    this.hoverService?.suspendForContextMenu();
    const resolution = this.resolveChannelHighlight(gate);
    this.gateContextMenuRequested.emit({
      gate,
      position: { x: event.clientX, y: event.clientY },
      ...(resolution
        ? { channel: { channelKeys: resolution.channelKeys, gates: resolution.gates } }
        : {}),
    });
  }

  /**
   * Hover-listener op alle center-shape elementen. Triggers center-hover
   * voor het center waartoe het element behoort. Gate-elementen stoppen
   * propagation zodat hover op een gate binnen een center het center-hover
   * NIET activeert (visuele scheiding tussen gate-info hover en center-hover).
   */
  private bindCenterHoverListeners(): void {
    for (const center of HD_BODYGRAPH_CENTERS) {
      const targets = this.findElementsByAliases([`${center}-shape`, `${center}-shape-defined`]);
      targets.forEach(target => {
        const enter = (event: Event) => {
          event.stopPropagation();
          this.hoverService?.setCenterKeynoteHover(center);
          this.hoverService?.setCenterHover(center);
          this.centerHovered.emit(center);
        };
        const leave = () => {
          this.hoverService?.setCenterKeynoteHover(null);
          this.hoverService?.setCenterHover(null);
          this.centerHovered.emit(null);
        };
        const contextMenu = (event: Event) => {
          // Zelfde debug-knop-gate als de gate-contextmenu: bij uitgeschakeld
          // laat de native browser-menu door (handig voor dev-tools Inspect).
          if (!this.contextMenuEnabled()) return;
          event.preventDefault();
          event.stopPropagation();
          const mouseEvent = event as MouseEvent;
          // Pauzeer hover-state mutaties vóór de emit: het CDK-overlay van
          // het menu triggert een mouseleave op deze center-shape die anders
          // de state zou clearen vóór de gebruiker "Freeze" kan kiezen.
          this.hoverService?.suspendForContextMenu();
          this.centerContextMenuRequested.emit({
            center,
            position: { x: mouseEvent.clientX, y: mouseEvent.clientY },
          });
        };
        const click = (event: Event) => {
          const mouseEvent = event as MouseEvent;
          if (mouseEvent.ctrlKey || mouseEvent.metaKey) {
            event.preventDefault();
            event.stopPropagation();
            this.tryHandleFreezeToggleShortcut(mouseEvent, { kind: 'center', center });
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          this.centerInfoRequested.emit({
            center,
            origin: { x: mouseEvent.clientX, y: mouseEvent.clientY },
          });
        };
        const keydown = (event: Event) => {
          const keyboardEvent = event as KeyboardEvent;
          if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;
          keyboardEvent.preventDefault();
          keyboardEvent.stopPropagation();
          this.centerInfoRequested.emit({
            center,
            origin: this.resolveElementCenterOrigin(target),
          });
        };
        target.addEventListener('mouseenter', enter);
        target.addEventListener('mouseleave', leave);
        target.addEventListener('contextmenu', contextMenu);
        target.addEventListener('click', click);
        target.addEventListener('keydown', keydown);
        target.classList.add('is-center-hover-target');
        this.listenerCleanups.push(() => {
          target.removeEventListener('mouseenter', enter);
          target.removeEventListener('mouseleave', leave);
          target.removeEventListener('contextmenu', contextMenu);
          target.removeEventListener('click', click);
          target.removeEventListener('keydown', keydown);
          target.removeAttribute('role');
          target.removeAttribute('tabindex');
          target.removeAttribute('aria-label');
          target.classList.remove('is-center-hover-target');
        });
      });
    }
  }

  /** Kanaalstuk-elementen van een poort: clickable channel-groepen + segment-lagen. */
  private findGateChannelTargets(gate: number): Element[] {
    const targets = new Set<Element>();
    this.findElementsByAliases(getHdBodygraphGateClickableChannelAliases(gate)).forEach(t => targets.add(t));
    this.findElementsByAliases(this.getGateSegmentAliases(gate)).forEach(t => targets.add(t));
    return [...targets];
  }

  private bindClickListener(target: Element, gate: number): void {
    const clickListener = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      const mouseEvent = event as MouseEvent;
      if (this.tryHandleFreezeToggleShortcut(mouseEvent, { kind: 'gate', gate })) {
        return;
      }
      this.handleGateClick(gate, mouseEvent);
    };
    const contextMenuListener = (event: Event) => {
      // Wanneer rechterklik-gedrag uitgeschakeld is (debug-knop), laten we
      // de native browser context-menu door zodat dev-tools "Inspect"
      // gebruikt kan worden op het SVG-element.
      if (!this.contextMenuEnabled()) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.handleGateContextMenu(gate, event as MouseEvent);
    };

    target.addEventListener('click', clickListener);
    target.addEventListener('contextmenu', contextMenuListener);
    target.classList.add('is-clickable');

    this.listenerCleanups.push(() => {
      target.removeEventListener('click', clickListener);
      target.removeEventListener('contextmenu', contextMenuListener);
      target.classList.remove('is-clickable');
    });
  }

  /**
   * Spiegelt het hover-effect van een verborgen gate-cijfer (vraagteken
   * onthullen) door op de gerelateerde elementen (kanaalstuk, segment,
   * selectie-laag) van de poort te luisteren. Bij hover wordt
   * `is-gate-hover-from-relation` op de gate-number-targets gezet.
   */
  private bindRelatedHoverListeners(target: Element, gateNumberTargets: Element[]): void {
    if (gateNumberTargets.length === 0) {
      return;
    }

    const enter = () => gateNumberTargets.forEach(t => t.classList.add('is-gate-hover-from-relation'));
    const leave = () => gateNumberTargets.forEach(t => t.classList.remove('is-gate-hover-from-relation'));

    target.addEventListener('mouseenter', enter);
    target.addEventListener('mouseleave', leave);

    this.listenerCleanups.push(() => {
      target.removeEventListener('mouseenter', enter);
      target.removeEventListener('mouseleave', leave);
      gateNumberTargets.forEach(t => t.classList.remove('is-gate-hover-from-relation'));
    });
  }

  private handleGateClick(gate: number, event: MouseEvent): void {
    const mode = this.interactionMode();

    this.gateClicked.emit(gate);

    if (mode === 'reveal-toggle') {
      this.emitToggledRevealedGate(gate);
      return;
    }

    if (mode !== 'cycle') {
      return;
    }

    if (this.primaryGateAction() === 'show-info') {
      this.gateInfoRequested.emit({ gate, origin: { x: event.clientX, y: event.clientY } });
      return;
    }

    this.configurationChange.emit(cycleHdBodygraphGateInConfig(this.configuration(), gate));
  }

  /**
   * Vangt de Ctrl+klik (Cmd+klik op macOS) toggle-shortcut af. Returnt `true`
   * wanneer de modifier aanwezig was én de service `enabled()` is — in dat
   * geval is de klik geconsumeerd voor freeze-toggle en mag de gewone
   * click-handler (gate-cycle / gate-info) niet meer lopen. Returnt `false`
   * voor een gewone klik zodat de caller normaal verder gaat.
   */
  private tryHandleFreezeToggleShortcut(event: MouseEvent, target: HdChartHoverTarget): boolean {
    if (!event.ctrlKey && !event.metaKey) return false;
    if (!this.hoverService?.enabled()) return false;
    this.hoverService.toggleFreezeForTarget(target);
    return true;
  }

  private handleGateContextMenu(gate: number, event: MouseEvent): void {
    if (this.interactionMode() !== 'cycle') {
      return;
    }
    // Pauzeer hover-state mutaties vóór de emit: het CDK-overlay van het
    // menu triggert een mouseleave op deze gate die anders de state zou
    // clearen vóór de gebruiker "Freeze" kan kiezen.
    this.hoverService?.suspendForContextMenu();
    this.gateContextMenuRequested.emit({ gate, position: { x: event.clientX, y: event.clientY } });
  }

  /**
   * Container-level safety net voor het geval een inner gate- of center-leave
   * niet (of te laat) bij de hover-service aankomt — bv. wanneer de cursor
   * snel naar een niet-hoverbaar gebied buiten de SVG beweegt. De service
   * debouncet zelf nog 72ms zodat een direct hierop volgende enter (chart →
   * planet-grid) geen tussenliggende clear veroorzaakt.
   */
  protected onSvgHostMouseLeave(): void {
    this.hoverService?.setKeynoteHover(null, 'chart-gate');
    this.hoverService?.setCenterKeynoteHover(null);
    this.hoverService?.clearForContainer(['chart-gate', 'chart-center']);
  }

  // ── Long-press = rechtsklik op touch (docs/MOBIEL.md §5) ─────────────────
  // iOS vuurt geen `contextmenu` bij een long-press op SVG-elementen; we
  // synthetiseren hem zelf zodat freeze en de poort-/center-contextmenu's ook
  // op touch bereikbaar zijn. Beweging (> slop) of loslaten annuleert; na een
  // afgevuurde long-press onderdrukt een capture-click-listener de emulated
  // click van het loslaten (anders zou bovenop het menu meteen de tap-actie
  // vuren).

  private static readonly TOUCH_LONG_PRESS_MS = 500;
  private static readonly TOUCH_LONG_PRESS_SLOP_PX = 12;

  private touchLongPressTimerId: ReturnType<typeof setTimeout> | null = null;
  private touchLongPressStart: { x: number; y: number } | null = null;

  protected onSvgHostPointerDown(event: PointerEvent): void {
    if (event.pointerType !== 'touch' || !this.contextMenuEnabled()) {
      return;
    }
    const target = (event.target as Element | null)?.closest('.is-clickable, .is-center-hover-target');
    if (target == null) {
      return;
    }
    this.cancelTouchLongPress();
    const position = { x: event.clientX, y: event.clientY };
    this.touchLongPressStart = position;
    this.touchLongPressTimerId = setTimeout(() => {
      this.touchLongPressTimerId = null;
      this.suppressNextClickAfterLongPress();
      target.dispatchEvent(new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        clientX: position.x,
        clientY: position.y,
      }));
    }, HdBodygraphChartComponent.TOUCH_LONG_PRESS_MS);
  }

  protected onSvgHostPointerMove(event: PointerEvent): void {
    if (this.touchLongPressTimerId == null || this.touchLongPressStart == null) {
      return;
    }
    const distance = Math.hypot(
      event.clientX - this.touchLongPressStart.x,
      event.clientY - this.touchLongPressStart.y,
    );
    if (distance > HdBodygraphChartComponent.TOUCH_LONG_PRESS_SLOP_PX) {
      this.cancelTouchLongPress();
    }
  }

  protected cancelTouchLongPress(): void {
    if (this.touchLongPressTimerId != null) {
      clearTimeout(this.touchLongPressTimerId);
      this.touchLongPressTimerId = null;
    }
    this.touchLongPressStart = null;
  }

  /** Slikt éénmalig de click die de browser na de long-press-release emuleert. */
  private suppressNextClickAfterLongPress(): void {
    const host = this.svgHost().nativeElement;
    const swallow = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      cleanup();
    };
    const cleanup = () => {
      clearTimeout(timeoutId);
      host.removeEventListener('click', swallow, { capture: true });
    };
    host.addEventListener('click', swallow, { capture: true });
    // Vangnetje: komt er geen click (menu opende, vinger schoof weg), ruim op.
    const timeoutId = setTimeout(cleanup, 700);
  }

  protected onSvgHostMouseMove(event: MouseEvent): void {
    if (!this.showHdKeynotes()) return;
    this.hdKeynoteTooltipPosition.set({ x: event.clientX, y: event.clientY });
  }

  /**
   * Gedelegeerde `mouseover`-safety-net op de svg-host: dekt het scenario
   * waarin de browser het inner `mouseleave` van een gate/center dropt bij
   * snelle cursorbeweging naar een niet-hoverbaar gebied **binnen** de
   * svg-host (lege ruimte in de SVG-canvas, achtergrond, …). De host-
   * `mouseleave` triggert in dat geval niet omdat de cursor de host nog
   * niet effectief verlaten heeft. `mouseover` bubbelt wél en vuurt
   * betrouwbaar zodra de cursor over een ander element komt.
   *
   * Hover-targets binnen de chart zijn gemarkeerd via `.is-clickable`
   * (gate-cijfers + gerelateerde kanaal/segment-elementen) en
   * `.is-center-hover-target` (center-shapes). Mouseover op een match → no-op
   * (cursor zit nog op een legitiem target). Anders → soft clear via de
   * service, die idempotent een 72ms leave-debounce plant.
   */
  protected onSvgHostMouseOver(event: MouseEvent): void {
    const target = event.target as Element | null;
    if (target?.closest('.is-clickable, .is-center-hover-target')) return;
    this.hoverService?.setKeynoteHover(null, 'chart-gate');
    this.hoverService?.setCenterKeynoteHover(null);
    this.hoverService?.softClearForContainer(['chart-gate', 'chart-center']);
  }

  private resolveGateKeynotePosition(gate: number): { readonly x: number; readonly y: number } | null {
    const targets = this.gateNumberTargetsByGate.get(gate);
    if (targets == null || targets.length === 0) return null;

    const rects = targets.map(target => target.getBoundingClientRect());
    const left = Math.min(...rects.map(rect => rect.left));
    const right = Math.max(...rects.map(rect => rect.right));
    const top = Math.min(...rects.map(rect => rect.top));
    return { x: left + (right - left) / 2, y: top };
  }

  private resolveCenterKeynotePosition(
    center: HdBodygraphCenter,
    isDefined: boolean,
  ): { readonly x: number; readonly y: number } | null {
    const alias = `${center}-shape${isDefined ? '-defined' : ''}`;
    const targets = this.findElementsByAliases([alias]);
    if (targets.length === 0) return null;

    const rects = targets.map(target => target.getBoundingClientRect());
    const left = Math.min(...rects.map(rect => rect.left));
    const right = Math.max(...rects.map(rect => rect.right));
    const top = Math.min(...rects.map(rect => rect.top));
    return { x: left + (right - left) / 2, y: top };
  }

  private buildHdKeynoteTooltipRows(target: HdKeynoteTooltipTarget): readonly HdKeynoteTooltipRow[] {
    const lang = this.contentLanguageService.effectiveLang();
    if (target.kind === 'gate') {
      return [this.buildGateKeynoteRow(target.gate, lang)];
    }

    const channels = this.resolveActivatedKeynoteChannels(target.gate);
    const channelRows = channels.flatMap(channel => {
      const translation = this.gateInfoService.channelTranslationIn(channel.key, lang);
      if (translation == null) return [];

      const partnerGate = channel.gates.find(gate => gate !== target.gate);
      return [{
        id: `channel-${channel.key}`,
        label: partnerGate == null ? `${target.gate}` : `${target.gate}–${partnerGate}`,
        text: [translation.name, translation.shortDescription].filter(Boolean).join(' — '),
      }];
    });

    const gates = [target.gate];
    for (const channel of channels) {
      for (const gate of channel.gates) {
        if (!gates.includes(gate)) {
          gates.push(gate);
        }
      }
    }

    return [
      ...channelRows,
      ...gates.map(gate => this.buildGateKeynoteRow(gate, lang)),
    ];
  }

  private resolveActivatedKeynoteChannelGates(gate: number): readonly number[] {
    const gates = [gate];
    for (const channel of this.resolveActivatedKeynoteChannels(gate)) {
      for (const channelGate of channel.gates) {
        if (!gates.includes(channelGate)) {
          gates.push(channelGate);
        }
      }
    }
    return gates;
  }

  private resolveActivatedKeynoteChannels(gate: number) {
    const activatedGates = this.hdKeynoteActivatedGates();
    return HD_BODYGRAPH_CHANNELS.filter(channel =>
      channel.gates.includes(gate) && channel.gates.every(channelGate => activatedGates.has(channelGate)),
    );
  }

  private buildGateKeynoteRow(gate: number, lang: string): HdKeynoteTooltipRow {
    const { keynote1, keynote2 } = this.gateInfoService.gateInfoIn(gate, lang).translations;
    return {
      id: `gate-${gate}`,
      label: `${gate}`,
      text: [keynote1, keynote2].filter(Boolean).join(', '),
    };
  }

  private get gateInfoService(): HdGateInfoService {
    return this.resolvedGateInfoService ??= this.injector.get(HdGateInfoService);
  }

  private get contentLanguageService(): HdContentLanguageService {
    return this.resolvedContentLanguageService ??= this.injector.get(HdContentLanguageService);
  }

  private get translocoService(): TranslocoService {
    return this.resolvedTranslocoService ??= this.injector.get(TranslocoService);
  }

  private hasCenterShapeTargets(): boolean {
    return HD_BODYGRAPH_CENTERS.some(center =>
      this.findElementsByAliases([`${center}-shape`, `${center}-shape-defined`]).length > 0,
    );
  }

  private centerAccessibilityLanguage(): string {
    this.contentLanguageService.translationsLoadedTick();
    return this.contentLanguageService.effectiveLang();
  }

  private syncCenterAccessibility(
    renderState: HdBodygraphRenderState,
    language: string,
  ): void {
    for (const center of HD_BODYGRAPH_CENTERS) {
      const baseTargets = this.findElementsByAliases([`${center}-shape`]);
      const definedTargets = this.findElementsByAliases([`${center}-shape-defined`]);
      const allTargets = new Set([...baseTargets, ...definedTargets]);
      if (allTargets.size === 0) continue;

      const visibleTargets = new Set(
        renderState.centerStates[center].isDefined ? definedTargets : baseTargets,
      );
      const ariaLabel = this.centerInfoAriaLabelIn(center, language);
      for (const target of allTargets) {
        if (visibleTargets.has(target)) {
          target.setAttribute('role', 'button');
          target.setAttribute('tabindex', '0');
          target.setAttribute('aria-label', ariaLabel);
          continue;
        }
        target.removeAttribute('role');
        target.removeAttribute('tabindex');
        target.removeAttribute('aria-label');
      }
    }
  }

  private centerInfoAriaLabelIn(center: HdBodygraphCenter, language: string): string {
    const centerName = this.translocoService.translate(
      `humanDesign.properties.centerNames.${center}`,
      undefined,
      language,
    );
    return this.translocoService.translate(
      'humanDesign.centerInfo.openAria',
      { center: centerName },
      language,
    );
  }

  private resolveElementCenterOrigin(target: Element): { x: number; y: number } {
    const rect = target.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  private findGateNumberTargets(root: Element, gate: number): Element[] {
    return Array.from(root.querySelectorAll('text'))
      .filter((text): text is SVGTextElement => text instanceof SVGTextElement)
      .filter(text => text.textContent?.replace(/\s+/g, '') === `${gate}`)
      .map(text => text.closest('g') ?? text);
  }

  private indexGateNumberTargets(root: Element): void {
    this.gateNumberTargetsByGate.clear();
    this.gateQuestionMarkTargetsByGate.clear();

    for (const gate of HD_BODYGRAPH_GATES) {
      const targets = this.findGateNumberTargets(root, gate);
      this.gateNumberTargetsByGate.set(gate, targets);
      this.gateQuestionMarkTargetsByGate.set(gate, this.injectGateQuestionMarks(targets));
    }
  }

  /**
   * Union-bbox per poort in viewBox-coördinaten. `getBBox()` levert lokale
   * coördinaten; `getCTM()` bleek daarbij de viewBox→CSS-pixel-schaal van de
   * gerenderde svg-box mee te nemen (alles ~linksboven samengedrukt), dus we
   * componeren net als de annotatielaag via screen-CTM's: element→scherm en
   * daarna scherm→viewBox via de inverse root-CTM. Verborgen of lege
   * elementen (bbox 0×0, of getBBox gooit) worden overgeslagen.
   */
  private computeGateAnchors(): ReadonlyMap<number, HdGateBlockRect> {
    const anchors = new Map<number, HdGateBlockRect>();
    const rootScreenCtm = this.svgRoot?.getScreenCTM();
    if (!rootScreenCtm) {
      return anchors;
    }
    const screenToViewBox = rootScreenCtm.inverse();
    this.gateNumberTargetsByGate.forEach((targets, gate) => {
      const rect = this.unionViewBoxRect(targets, screenToViewBox);
      if (rect) {
        anchors.set(gate, rect);
      }
    });
    return anchors;
  }

  /**
   * Union-bbox van een set elementen in viewBox-coördinaten via de
   * screen-CTM-compositie (zie {@link computeGateAnchors}). `null` wanneer
   * geen enkel element een meetbare bbox oplevert.
   */
  private unionViewBoxRect(targets: readonly Element[], screenToViewBox: DOMMatrix): HdGateBlockRect | null {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const target of targets) {
      if (!(target instanceof SVGGraphicsElement)) {
        continue;
      }
      let box: DOMRect;
      try {
        box = target.getBBox();
      } catch {
        continue;
      }
      if (box.width === 0 || box.height === 0) {
        continue;
      }
      const screenCtm = target.getScreenCTM();
      if (!screenCtm) {
        continue;
      }
      const toViewBox = screenToViewBox.multiply(screenCtm);
      const corners: Array<[number, number]> = [
        [box.x, box.y],
        [box.x + box.width, box.y],
        [box.x, box.y + box.height],
        [box.x + box.width, box.y + box.height],
      ];
      for (const [cornerX, cornerY] of corners) {
        const p = new DOMPoint(cornerX, cornerY).matrixTransform(toViewBox);
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      }
    }
    if (minX < maxX && minY < maxY) {
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
    return null;
  }

  /**
   * Meet de ankers voor de monopool-laag: G-center (zelf), hoofd en ajna
   * (shape-bboxen). `null` zolang een center-shape niet meetbaar is
   * (bv. stub-SVG's zonder deze shapes).
   */
  private measureMonopoleLayerGeometry(): HdMonopoleLayerGeometry | null {
    const rootScreenCtm = this.svgRoot?.getScreenCTM();
    if (!rootScreenCtm) {
      return null;
    }
    const screenToViewBox = rootScreenCtm.inverse();
    const gCenter = this.unionViewBoxRect(this.findElementsByAliases(['zelf-shape', 'zelf-shape-defined']), screenToViewBox);
    const head = this.unionViewBoxRect(this.findElementsByAliases(['hoofd-shape', 'hoofd-shape-defined']), screenToViewBox);
    const ajna = this.unionViewBoxRect(this.findElementsByAliases(['ajna-shape', 'ajna-shape-defined']), screenToViewBox);
    if (!gCenter || !head || !ajna) {
      return null;
    }
    return { gCenter, head, ajna };
  }

  /**
   * Injecteert of verwijdert de monopool & bewustzijnskristallen-laag. De laag
   * komt als laatste SVG-child (bovenop centers/kanalen) en is met
   * `pointer-events: none` inert voor het klik- en hovergedrag van de chart.
   */
  private applyMonopoleLayer(visible: boolean): void {
    const svg = this.svgRoot;
    if (!svg) {
      return;
    }

    svg.querySelector(`#${HD_MONOPOLE_LAYER_ID}`)?.remove();
    this.monopoleTooltipPosition.set(null);

    if (!visible) {
      return;
    }

    const geometry = this.measureMonopoleLayerGeometry();
    if (!geometry) {
      return;
    }

    const template = document.createElementNS(SVG_NAMESPACE, 'g');
    template.innerHTML = buildHdMonopoleLayerMarkup(geometry, this.monopoleLayerUid);
    const layer = template.firstElementChild;
    if (layer) {
      svg.appendChild(layer);
      this.bindMonopoleTooltip(layer);
    }
  }

  /**
   * Hover-tooltip en klik op het monopool-icoon en de twee kristallen. De laag
   * zelf is pointer-inert; enkel die drie elementen krijgen weer pointer-events
   * zodat de rest van de chart normaal klik- en hoverbaar blijft. Een klik opent
   * het paneel "De drie dragers van bewustzijn" ({@link monopoleInfoRequested});
   * `stopPropagation` houdt de klik weg van het center eronder (ajna/hoofd
   * zouden anders hun eigen info-paneel openen). De listeners verdwijnen mee
   * met de laag-DOM.
   */
  private bindMonopoleTooltip(layer: Element): void {
    const targets: readonly { selector: string; key: HdMonopoleTooltipKey }[] = [
      { selector: '.hd-monopole-icon', key: 'tooltip' },
      { selector: '.hd-monopole-crystal--design', key: 'designCrystalTooltip' },
      { selector: '.hd-monopole-crystal--personality', key: 'personalityCrystalTooltip' },
    ];
    for (const { selector, key } of targets) {
      const element = layer.querySelector(selector);
      if (!(element instanceof SVGElement)) {
        continue;
      }
      element.style.pointerEvents = 'all';
      element.style.cursor = 'pointer';
      const track = (event: MouseEvent): void =>
        this.monopoleTooltipPosition.set({ x: event.clientX, y: event.clientY, key });
      element.addEventListener('mouseenter', track);
      element.addEventListener('mousemove', track);
      element.addEventListener('mouseleave', () => this.monopoleTooltipPosition.set(null));
      element.addEventListener('click', (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        this.monopoleTooltipPosition.set(null);
        this.monopoleInfoRequested.emit({ origin: { x: event.clientX, y: event.clientY } });
      });
    }
  }

  /**
   * Injecteert of verwijdert de Locks & Keys-laag (spec 2026-10-10 §3):
   * - ringen per center als SVG-children van de root, dus bóven alle
   *   poortcirkels. BELANGRIJK: niet terug in de centergroepen zetten — daar
   *   schilderen later getekende buurcirkels over de ring heen (Stevens
   *   screenshot 2026-10-10: de achtergrond van 3 over de ringen van 42 en 9).
   *   De ringen staan in viewBox-coördinaten; de spec toetst ook de gespiegelde
   *   solar-plexusgroep;
   * - badges (slot in het G-center, sleutel naast elk key-center) als laatste
   *   SVG-child, pointer-inert, net als de monopool-laag.
   * Het monopool bezet het midden van het G-center al: staat het aan, dan
   * blijft de slot-badge weg (de ringen blijven).
   */
  private applyLocksKeysLayer(
    overlay: HdBodygraphLocksKeysOverlay | null,
    activatedGates: ReadonlySet<number>,
    monopoleVisible: boolean,
  ): void {
    const svg = this.svgRoot;
    if (!svg) {
      return;
    }

    svg.querySelectorAll(`.${LOCKS_KEYS_RINGS_CLASS}, .${HD_LOCKS_KEYS_BADGE_LAYER_CLASS}`)
      .forEach(element => element.remove());
    this.locksKeysHoverTargets.length = 0;

    if (overlay == null) {
      return;
    }
    const rootScreenCtm = svg.getScreenCTM();
    if (!rootScreenCtm) {
      return;
    }
    const screenToViewBox = rootScreenCtm.inverse();
    const plan = planHdBodygraphLocksKeysOverlay(overlay, activatedGates);

    // Ringen gegroepeerd per center (voor de hover-dim per center).
    const ringsByCenter = new Map<HdBodygraphCenter, string>();
    const addRing = (gate: number, kind: 'lock' | 'key'): void => {
      const center = HD_BODYGRAPH_GATE_TO_CENTER[gate];
      const ellipse = this.gateCircleGeometry(gate, screenToViewBox);
      if (center == null || ellipse == null) {
        return;
      }
      ringsByCenter.set(center, (ringsByCenter.get(center) ?? '') + buildHdLocksKeysGateRingMarkup(kind, gate, ellipse));
    };
    plan.lockGates.forEach(gate => addRing(gate, 'lock'));
    plan.keyGates.forEach(gate => addRing(gate, 'key'));

    ringsByCenter.forEach((markup, center) => {
      const rings = document.createElementNS(SVG_NAMESPACE, 'g');
      rings.setAttribute('class', LOCKS_KEYS_RINGS_CLASS);
      rings.setAttribute('pointer-events', 'none');
      rings.setAttribute('aria-hidden', 'true');
      rings.setAttribute('data-center', center);
      rings.innerHTML = markup;
      svg.appendChild(rings);
      this.locksKeysHoverTargets.push(rings);
    });

    const centerRect = (center: HdBodygraphCenter): HdLocksKeysLayerRect | null =>
      this.unionViewBoxRect(this.findElementsByAliases([`${center}-shape`, `${center}-shape-defined`]), screenToViewBox);
    const keyBadges = plan.keyCenters.flatMap(center => {
      const rect = centerRect(center);
      const position = rect ? hdLocksKeysKeyBadgeCenter(center, rect) : null;
      return position ? [{ center, ...position }] : [];
    });
    const gCenter = plan.showLockBadge && !monopoleVisible ? centerRect('zelf') : null;
    if (gCenter != null || keyBadges.length > 0) {
      const template = document.createElementNS(SVG_NAMESPACE, 'g');
      template.innerHTML = buildHdLocksKeysBadgeLayerMarkup(gCenter, keyBadges);
      const layer = template.firstElementChild;
      if (layer) {
        svg.appendChild(layer);
        layer.querySelectorAll('[data-center]').forEach(badge => this.locksKeysHoverTargets.push(badge));
      }
    }
    // Een lopende hover-rand blijft boven de net (her)tekende ringen.
    this.raiseHoverOverlayLayers();
  }

  /**
   * Ellips van een poortcirkel in viewBox-coördinaten (gecachet: de geometrie
   * is statisch t.o.v. de viewBox). `null` zolang de SVG niet meetbaar is.
   */
  private gateCircleGeometry(gate: number, screenToViewBox?: DOMMatrix): HdLocksKeysLayerEllipse | null {
    const cached = this.gateCircleGeometryByGate.get(gate);
    if (cached) {
      return cached;
    }
    const toViewBox = screenToViewBox ?? this.svgRoot?.getScreenCTM()?.inverse();
    const selected = this.findElementsByAliases(getHdBodygraphGateSelectedAliases(gate));
    if (!toViewBox || selected.length === 0) {
      return null;
    }
    const shapes = selected.flatMap(element => Array.from(element.querySelectorAll('ellipse, circle')));
    const rect = this.unionViewBoxRect(shapes, toViewBox);
    if (!rect) {
      return null;
    }
    const ellipse = { cx: rect.x + rect.width / 2, cy: rect.y + rect.height / 2, rx: rect.width / 2, ry: rect.height / 2 };
    this.gateCircleGeometryByGate.set(gate, ellipse);
    return ellipse;
  }

  /**
   * Blauwe hover-rand van de gehoverde poort(en) als kopie in een bovenste
   * laag. De rand zelf is een stroke op de poortcirkel (`.is-hover-source-gate`)
   * en verdween half onder later getekende buurcirkels (Steven 2026-10-10); de
   * kopie ligt boven alles, de klasse blijft voor de gesplitste helften.
   * Enkel een paar elementen per hover-wissel; geometrie uit de cache.
   */
  private applyHoverGateRings(gates: readonly number[]): void {
    if (gates.length === 0) {
      this.hoverGateRingLayer?.replaceChildren();
      return;
    }
    const svg = this.svgRoot;
    if (!svg) {
      return;
    }
    if (this.hoverGateRingLayer == null || this.hoverGateRingLayer.ownerSVGElement !== svg) {
      const layer = document.createElementNS(SVG_NAMESPACE, 'g');
      layer.setAttribute('class', HOVER_GATE_RING_LAYER_CLASS);
      layer.setAttribute('pointer-events', 'none');
      layer.setAttribute('aria-hidden', 'true');
      this.hoverGateRingLayer = layer;
    }
    const rings = gates.flatMap(gate => {
      const ellipse = this.gateCircleGeometry(gate);
      if (!ellipse) {
        return [];
      }
      const ring = document.createElementNS(SVG_NAMESPACE, 'ellipse');
      ring.setAttribute('class', 'hd-hover-gate-ring');
      ring.setAttribute('cx', `${ellipse.cx}`);
      ring.setAttribute('cy', `${ellipse.cy}`);
      ring.setAttribute('rx', `${ellipse.rx}`);
      ring.setAttribute('ry', `${ellipse.ry}`);
      return [ring];
    });
    this.hoverGateRingLayer.replaceChildren(...rings);
    this.raiseHoverOverlayLayers();
  }

  /**
   * Outline rond het kanaalstuk van de gehighlighte poort(en): app-setting
   * "poortstukken highlighten". Het silhouet is wat er van het stuk zichtbaar
   * is: voor de integratiepoorten de lagen die de render hun toewijst (zodat
   * bv. kanaal 20-34 inclusief brugstuk en eigen corner-cap één rand krijgt),
   * min wat lagen van andere poorten erover tekenen. Zie
   * {@link HdGatePieceOutlineLayer}.
   */
  private applyHoverGatePieceOutlines(gates: readonly number[]): void {
    if (gates.length === 0 || !(this.hoverService?.highlightGatePieces() ?? false)) {
      this.gatePieceOutlineLayer?.clear();
      return;
    }
    const svg = this.svgRoot;
    if (!svg) {
      return;
    }
    if (this.gatePieceOutlineLayer == null || this.gatePieceOutlineLayer.svg !== svg) {
      this.gatePieceOutlineLayer = new HdGatePieceOutlineLayer(
        svg,
        this.gatePieceOutlineMaskId,
        this.collectGatePieceOutlineExclusions(),
      );
    }
    const own: Element[] = [];
    let touchesIntegration = false;
    for (const gate of gates) {
      const integrationLeaves = this.outlineIntegrationLeavesByGate.get(gate);
      touchesIntegration ||= integrationLeaves != null;
      own.push(...(integrationLeaves ?? this.hoverPieceLeavesByGate.get(gate) ?? []));
    }
    // Enkel in het integratiegebied liggen lagen van andere poorten over een
    // eigen stuk; die gommen daar het zichtbare deel weg.
    this.gatePieceOutlineLayer.render(own, touchesIntegration ? this.integrationClusterLeaves : []);
    this.raiseHoverOverlayLayers();
  }

  /** Centers en poortcirkels: daar mag de poortstuk-outline nooit over tekenen. */
  private collectGatePieceOutlineExclusions(): Element[] {
    const roots = [
      ...Array.from(this.hoverSourceCenterMarkersByCenter.values()).flat(),
      ...Array.from(this.hoverSourceGateMarkersByGate.values()).flat(),
    ];
    const leaves = new Set<Element>();
    roots.forEach(root => this.collectDimLeavesIn(root, HD_GATE_PIECE_SHAPE_SELECTOR, leaves));
    return [...leaves].filter(leaf => !leaf.classList.contains(SPLIT_GATE_CIRCLE_HALF_CLASS));
  }

  /**
   * Houdt de hover-overlays als laatste SVG-children (boven ringen en badges):
   * eerst de poortstuk-outline, daarboven de poortcirkel-rand. Verplaatst
   * enkel wanneer de volgorde niet al klopt.
   */
  private raiseHoverOverlayLayers(): void {
    const svg = this.svgRoot;
    if (!svg) {
      return;
    }
    const layers = [this.gatePieceOutlineLayer?.layer, this.hoverGateRingLayer]
      .filter((layer): layer is SVGGElement => layer != null && layer.childElementCount > 0);
    let expected = svg.lastElementChild;
    let inPlace = true;
    for (let index = layers.length - 1; index >= 0; index--) {
      if (expected !== layers[index]) {
        inPlace = false;
        break;
      }
      expected = expected?.previousElementSibling ?? null;
    }
    if (!inPlace) {
      layers.forEach(layer => svg.appendChild(layer));
    }
  }

  /**
   * Hover-dim voor de Locks & Keys-laag: ringen en badges van centers buiten
   * de hover-focus vervagen mee (eigen klasse, want de laag zit niet in de
   * vooraf berekende dim-caches). `null` = geen hover, alles weer helder.
   */
  private applyLocksKeysHoverDim(targetCenters: readonly HdBodygraphCenter[] | null): void {
    for (const target of this.locksKeysHoverTargets) {
      const center = target.getAttribute('data-center') as HdBodygraphCenter | null;
      const dimmed = targetCenters != null && (center == null || !targetCenters.includes(center));
      target.classList.toggle(LOCKS_KEYS_HOVER_DIM_CLASS, dimmed);
    }
  }

  /**
   * Voegt per gate-cijfer-target een duplicaat `<text>` met "?" toe binnen
   * dezelfde `<g>`. Het vraagteken neemt de positie en stijl van het origineel
   * over en wordt enkel zichtbaar gemaakt via CSS bij hover op een verborgen
   * gate-cijfer (zie `.is-gate-number-hidden:hover .gate-question-mark`).
   */
  private injectGateQuestionMarks(targets: Element[]): SVGTextElement[] {
    const created: SVGTextElement[] = [];

    targets.forEach(target => {
      if (!(target instanceof SVGElement)) {
        return;
      }

      const originalText = target.querySelector('text');
      if (!(originalText instanceof SVGTextElement)) {
        return;
      }

      const questionMark = originalText.cloneNode(false) as SVGTextElement;
      questionMark.textContent = '?';
      questionMark.classList.add('gate-question-mark');
      questionMark.setAttribute('aria-hidden', 'true');
      questionMark.style.pointerEvents = 'none';
      target.appendChild(questionMark);
      created.push(questionMark);
    });

    return created;
  }

  /**
   * Geeft elke poortcirkel een kloon die tot zijn linkerhelft geclipt is, zodat
   * een gesplitste cirkel (design + personality, of basis + transit) uit twee
   * effen vormen bestaat. Zie {@link applySplitGateCircleColors}.
   *
   * BELANGRIJK — niet terugdraaien naar een `url(#gradient)`-fill op de cirkel.
   * Een paint-server-verwijzing is niet interpoleerbaar met een kleur, dus de
   * 120ms fill/stroke-transitie van `.is-hover-dim` start voor zo'n cirkel niet:
   * hij springt in hetzelfde frame naar `#e6e6e6` terwijl zijn witte cijfer nog
   * 120ms naar grijs vervaagt. Resultaat was een korte "wit bolletje"-flits op
   * precies de gesplitste poorten (gemeld op 38 en 9) telkens hun center
   * gedimd raakte, terwijl effen cirkels netjes meevervaagden. Met twee effen
   * vormen interpoleren beide helften zoals elke andere cirkel. Gedekt door de
   * specs "gesplitste poortcirkels" in de component-spec.
   *
   * Moet vóór {@link buildHoverHighlightCaches} lopen: de klonen zijn gewone
   * leaves binnen de selected-groep en komen zo vanzelf in de keep/dim-sets.
   */
  private injectSplitGateCircleHalves(svg: SVGSVGElement): void {
    this.splitGateCircleHalvesByGate.clear();

    const clipPath = document.createElementNS(SVG_NAMESPACE, 'clipPath');
    clipPath.id = this.splitGateCircleClipId;
    clipPath.setAttribute('clipPathUnits', 'objectBoundingBox');
    // Linkerhelft plus marge links/boven/onder: de objectBoundingBox sluit de
    // stroke uit, en die moet aan de buitenrand van de helft zichtbaar blijven
    // (o.a. de blauwe `.is-hover-source-gate`-rand).
    const leftHalf = document.createElementNS(SVG_NAMESPACE, 'rect');
    leftHalf.setAttribute('x', '-1');
    leftHalf.setAttribute('y', '-1');
    leftHalf.setAttribute('width', '1.5');
    leftHalf.setAttribute('height', '3');
    clipPath.append(leftHalf);
    const defs = document.createElementNS(SVG_NAMESPACE, 'defs');
    defs.append(clipPath);
    svg.insertBefore(defs, svg.firstChild);

    for (const gate of HD_BODYGRAPH_GATES) {
      const halves: SVGElement[] = [];
      this.findElementsByAliases(getHdBodygraphGateSelectedAliases(gate)).forEach(element => {
        element.querySelectorAll('ellipse, circle').forEach(shape => {
          const half = shape.cloneNode(false) as SVGElement;
          half.classList.add(SPLIT_GATE_CIRCLE_HALF_CLASS);
          half.setAttribute('clip-path', `url(#${this.splitGateCircleClipId})`);
          half.setAttribute('aria-hidden', 'true');
          shape.after(half);
          halves.push(half);
        });
      });
      this.splitGateCircleHalvesByGate.set(gate, halves);
    }
  }

  private applyGateNumberVisibility(
    interactionMode: HdBodygraphInteractionMode,
    revealedGates: ReadonlySet<number>,
    errorFlashGates: ReadonlySet<number>,
    correctFlashGates: ReadonlySet<number>,
    selectionHighlightGates: ReadonlySet<number>,
  ): void {
    const hidesNumbers = interactionMode !== 'cycle';

    this.gateNumberTargetsByGate.forEach((targets, gate) => {
      const isVisibleByReveal =
        revealedGates.has(gate)
        || errorFlashGates.has(gate)
        || correctFlashGates.has(gate)
        || selectionHighlightGates.has(gate);
      const shouldHide = hidesNumbers && !isVisibleByReveal;

      targets.forEach(target => {
        this.setGateNumberHiddenState(target, shouldHide);
      });
    });
  }

  /**
   * Verbergt het gate-cijfer visueel maar houdt het klikbaar en hover-baar.
   * We gebruiken een CSS-klasse (`is-gate-number-hidden`) i.p.v. inline
   * `visibility: hidden`, zodat hover-effecten (vraagteken-onthulling) blijven
   * werken. `pointer-events: all` wordt op het element en zijn kinderen
   * geforceerd omdat SVG's standaard `visiblePainted` anders geen klikken meer
   * doorlaat zodra er niets meer geverfd wordt.
   */
  private setGateNumberHiddenState(target: Element, hidden: boolean): void {
    if (!(target instanceof SVGElement || target instanceof HTMLElement)) {
      return;
    }

    target.classList.toggle('is-gate-number-hidden', hidden);
    target.style.pointerEvents = hidden ? 'all' : '';

    target.querySelectorAll<SVGElement | HTMLElement>('*').forEach(child => {
      const isQuestionMark = child instanceof SVGElement && child.classList.contains('gate-question-mark');
      child.style.pointerEvents = isQuestionMark ? 'none' : (hidden ? 'all' : '');
    });
  }

  /**
   * Kanaalbreuk na de globale shift (19/49 bij een geboorte vanaf 15-02-2027,
   * zie hd-global-shift.ts): beide poorten blijven hangende poorten, maar het
   * kanaal vormt zich niet. Twee volle kanaalhelften zouden er als een
   * gedefinieerd kanaal uitzien, dus de helften van beide poorten krijgen
   * `is-shift-broken-channel` (gedimd). Bij elke render opnieuw gezet of gewist.
   */
  private applyShiftBrokenChannels(renderState: HdBodygraphRenderState): void {
    for (const channelKey of HD_POST_SHIFT_BROKEN_CHANNEL_KEYS) {
      const channel = renderState.channelStates[channelKey];
      if (channel == null) continue;
      const isBroken = channel.isBrokenByShift === true;
      for (const gate of channel.gates) {
        this.findElementsByAliases(this.getGateSegmentAliases(gate))
          .forEach(target => target.classList.toggle('is-shift-broken-channel', isBroken));
      }
    }
  }

  /**
   * Past de fout-flash kleur toe op de gate-cijfers van de meegegeven poorten.
   * De timing (auto-reset na 2s) wordt door de orchestrator beheerd; de chart
   * weerspiegelt enkel de huidige waarde van `errorFlashGates`. De input is een
   * set zodat zowel de Poortenjacht (één poort) als de Kanalenjacht (twee
   * poorten van een gate-paar zonder geldig kanaal) hetzelfde mechanisme
   * kunnen gebruiken.
   */
  private applyErrorFlash(errorFlashGates: ReadonlySet<number>): void {
    this.gateNumberTargetsByGate.forEach((targets, gate) => {
      const isFlashing = errorFlashGates.has(gate);

      targets.forEach(target => {
        target.classList.toggle('is-error-flash', isFlashing);
      });
    });
  }

  /**
   * Past de correct-flash CSS-klasse toe op de gate-cijfers van de meegegeven
   * poorten. Wordt door de orchestrator gezet bij een correct poort-antwoord
   * en na een korte timing terug gewist. Combineert met de render-overlay
   * (zie {@link applyCorrectFlashGatesOverlay}) die de poort + kanaalstuk
   * tegelijk in de highlight-kleur toont alsof er een activatie is.
   */
  private applyCorrectFlash(correctFlashGates: ReadonlySet<number>): void {
    this.gateNumberTargetsByGate.forEach((targets, gate) => {
      const isFlashing = correctFlashGates.has(gate);

      targets.forEach(target => {
        target.classList.toggle('is-correct-flash', isFlashing);
      });
    });
  }

  /**
   * Markeert de gate-cijfers van de meegegeven poorten als "geselecteerd" voor
   * de Kanalenjacht-game (eerste poort van een twee-klik-flow). De CSS-klasse
   * `is-selection-highlight` zorgt voor de blauwe accent-kleur en wordt op
   * dezelfde gate-number-targets toegepast als de fout-flash, zodat het
   * mechanisme analoog blijft.
   */
  private applySelectionHighlight(selectionHighlightGates: ReadonlySet<number>): void {
    this.gateNumberTargetsByGate.forEach((targets, gate) => {
      const isSelected = selectionHighlightGates.has(gate);

      targets.forEach(target => {
        target.classList.toggle('is-selection-highlight', isSelected);
      });
    });
  }

  /**
   * Leest de huidige effectieve hover-state uit de service en past die
   * synchroon toe op de SVG. Aangeroepen door de `onStateChange`-listener
   * (zie constructor) en éénmalig na de SVG-load zodat een reeds bevroren /
   * initiële state meteen zichtbaar is. No-op tot de SVG geladen is.
   */
  private applyHoverFromService(): void {
    if (!this.svgLoaded) return;
    // BELANGRIJK — `untracked`, niet weghalen: deze methode wordt ook aan het eind
    // van `applyRenderState` aangeroepen, dus bínnen het render-`effect()`. Zonder
    // untracked tracken de hover-signals (enabled/effectiveGates/effectiveCenters,
    // en dieper dimsChart/highlightGatePieces) dat effect, waardoor élke
    // hover-publicatie, ook een leave, de volledige chart hertekende: ~18 ms extra
    // per hover bovenop de dim-pass (gemeten 2026-10-13, basis-paneel-kaartjes
    // onder een scrollende cursor). Hover loopt enkel via de synchrone listener.
    // Zie hd-bodygraph-chart.component.spec.ts ("render-effect blijft stil").
    untracked(() => {
      const enabled = this.hoverService?.enabled() ?? false;
      const gates = enabled ? (this.hoverService?.effectiveGates() ?? null) : null;
      const centers = enabled ? (this.hoverService?.effectiveCenters() ?? null) : null;
      this.applyHoverHighlight(gates, centers);
    });
  }

  /**
   * Hover-highlight gedrag (zie `docs/human-design/hd-chart-hover-highlight.md`).
   *
   * Past CSS-klassen toe op de SVG-root en op de relevante "keep"-elementen
   * zodat de rest visueel gedimd wordt en het gehoverde element extra
   * highlights krijgt (border-cue op het center-shape of de gate-cirkel).
   *
   * Performance: het hover-pad gebruikt enkel de pre-computed sets uit
   * {@link buildHoverHighlightCaches}. Geen `querySelectorAll` of `closest`
   * per hover-event, en classes worden enkel op echt-gewijzigde elementen
   * ge-toggled (delta-tracking via `activeDimmedLeaves` /
   * `activeSourceMarkers`). Hierdoor blijft de chart synchroon met de
   * Signal-gestuurde planet grid.
   */
  private applyHoverHighlight(
    effectiveGates: ReadonlySet<number> | null,
    effectiveCenters: ReadonlySet<HdBodygraphCenter> | null,
  ): void {
    const svg = this.svgRoot;
    if (!svg) return;

    this.clearActiveHoverMarkers();

    const gateCount = effectiveGates?.size ?? 0;
    const centerCount = effectiveCenters?.size ?? 0;
    if (gateCount === 0 && centerCount === 0) {
      svg.classList.remove('is-hover-active');
      this.clearActiveDimmedLeaves();
      return;
    }

    const targetCenters = this.resolveHoverTargetCentersFromUnion(effectiveGates, effectiveCenters);
    if (targetCenters.length === 0) {
      svg.classList.remove('is-hover-active');
      this.clearActiveDimmedLeaves();
      return;
    }

    const hoveredGates = effectiveGates ? Array.from(effectiveGates) : [];

    this.applyHoverSourceMarkers(effectiveCenters, hoveredGates);

    svg.classList.add('is-hover-active');

    // "Dimmen bij hover" uit (app-setting), of "enkel één kant dimmen" bij een
    // hover vanuit de chart: enkel de source-markers hierboven; geen enkele
    // leaf krijgt `is-hover-dim`. De clear houdt de delta-state consistent
    // wanneer een setting toggelt terwijl er al een hover/freeze actief is
    // (de setters lopen via notifyStateChange).
    if (this.hoverService?.dimsChart() ?? true) {
      const keepUnion = this.buildHoverKeepUnion(targetCenters, hoveredGates);
      this.applyHoverDimDelta(keepUnion);
      this.applyLocksKeysHoverDim(targetCenters);
    } else {
      this.clearActiveDimmedLeaves();
    }
  }

  /**
   * Bouwt de union van centers waarvoor de keep-set actief is. Dit zijn
   * (a) de expliciet effectieve centers (center-item of center-hover) en
   * (b) de centers waartoe de effectieve gates behoren — die laatste zorgen
   * dat bij een gate-hover/freeze het kanaalstuk van het center ook deel
   * van de keep-set wordt.
   */
  private resolveHoverTargetCentersFromUnion(
    effectiveGates: ReadonlySet<number> | null,
    effectiveCenters: ReadonlySet<HdBodygraphCenter> | null,
  ): HdBodygraphCenter[] {
    const centers: HdBodygraphCenter[] = [];
    if (effectiveCenters) {
      effectiveCenters.forEach(c => {
        if (!centers.includes(c)) centers.push(c);
      });
    }
    if (effectiveGates) {
      effectiveGates.forEach(gate => {
        const center = HD_BODYGRAPH_GATE_TO_CENTER[gate];
        if (center != null && !centers.includes(center)) centers.push(center);
      });
    }
    return centers;
  }

  private buildHoverKeepUnion(targetCenters: HdBodygraphCenter[], hoveredGates: number[]): Set<Element> {
    const keepUnion = new Set<Element>();
    for (const center of targetCenters) {
      this.hoverKeepLeavesByCenter.get(center)?.forEach(el => keepUnion.add(el));
    }
    for (const gate of hoveredGates) {
      this.hoverKeepLeavesByGate.get(gate)?.forEach(el => keepUnion.add(el));
      this.hoverIntegrationKeepLeavesByGate.get(gate)?.forEach(el => keepUnion.add(el));
    }
    return keepUnion;
  }

  /**
   * Past source-markers toe op de effectieve set. In de additieve flow zijn
   * gate- en center-markers niet langer wederzijds exclusief: een bevroren
   * gate + bevroren center renderen beide hun eigen source-marker.
   */
  private applyHoverSourceMarkers(
    effectiveCenters: ReadonlySet<HdBodygraphCenter> | null,
    hoveredGates: number[],
  ): void {
    for (const gate of hoveredGates) {
      const markers = this.hoverSourceGateMarkersByGate.get(gate) ?? [];
      markers.forEach(el => {
        el.classList.add('is-hover-source-gate');
        this.activeSourceMarkers.add(el);
      });
    }
    this.applyHoverGateRings(hoveredGates);
    this.applyHoverGatePieceOutlines(hoveredGates);
    if (effectiveCenters) {
      effectiveCenters.forEach(center => {
        const markers = this.hoverSourceCenterMarkersByCenter.get(center) ?? [];
        markers.forEach(el => {
          el.classList.add('is-hover-source-center');
          this.activeSourceMarkers.add(el);
        });
      });
    }
  }

  /**
   * Past de dim-state aan via symmetrische verschil-set:
   *  - Un-dim: enkel iteratie over `nextKeepUnion` (typisch enkele tientallen
   *    elementen) — verwijder de class waar al een dim actief was.
   *  - Dim: enkel iteratie over de vorige keep-union (eveneens klein) — voeg
   *    de class toe waar het element niet langer in de keep-set zit.
   * Enkel de eerste hover na een clear doet een full-pass over alle
   * {@link hoverDimLeaves}; daarna blijven hover-transities O(klein).
   */
  private applyHoverDimDelta(nextKeepUnion: Set<Element>): void {
    for (const leaf of nextKeepUnion) {
      if (this.activeDimmedLeaves.has(leaf)) {
        leaf.classList.remove('is-hover-dim');
        this.activeDimmedLeaves.delete(leaf);
      }
    }

    const previousKeepUnion = this.currentHoverKeepUnion;
    if (previousKeepUnion === null) {
      for (const leaf of this.hoverDimLeaves) {
        if (!nextKeepUnion.has(leaf)) {
          leaf.classList.add('is-hover-dim');
          this.activeDimmedLeaves.add(leaf);
        }
      }
    } else {
      for (const leaf of previousKeepUnion) {
        if (!nextKeepUnion.has(leaf)) {
          leaf.classList.add('is-hover-dim');
          this.activeDimmedLeaves.add(leaf);
        }
      }
    }

    this.currentHoverKeepUnion = nextKeepUnion;
  }

  private clearActiveHoverMarkers(): void {
    this.activeSourceMarkers.forEach(el => {
      el.classList.remove('is-hover-source-gate', 'is-hover-source-center');
    });
    this.activeSourceMarkers.clear();
    this.applyHoverGateRings([]);
    this.applyHoverGatePieceOutlines([]);
  }

  private clearActiveDimmedLeaves(): void {
    this.activeDimmedLeaves.forEach(el => el.classList.remove('is-hover-dim'));
    this.activeDimmedLeaves.clear();
    this.currentHoverKeepUnion = null;
    this.applyLocksKeysHoverDim(null);
  }

  /**
   * Bouwt eenmalig de hover-highlight caches op na het laden van de SVG.
   * Voor elk mogelijk hover-target (center of gate) wordt een "keep-set"
   * van leaf-elementen voorberekend die helder moeten blijven. Het
   * hover-pad doet dan enkel nog `Set.has()` lookups in plaats van DOM
   * traversals per event.
   */
  private buildHoverHighlightCaches(svg: Element): void {
    this.hoverDimLeaves.length = 0;
    this.hoverKeepLeavesByCenter.clear();
    this.hoverKeepLeavesByGate.clear();
    this.hoverIntegrationKeepLeavesByGate.clear();
    this.hoverSourceCenterMarkersByCenter.clear();
    this.hoverSourceGateMarkersByGate.clear();
    this.hoverPieceLeavesByGate.clear();
    this.integrationClusterLeaves.length = 0;
    this.activeDimmedLeaves.clear();
    this.activeSourceMarkers.clear();
    this.currentHoverKeepUnion = null;

    const dimSelector = HOVER_DIM_LEAF_SELECTOR;
    svg.querySelectorAll(dimSelector).forEach(leaf => this.hoverDimLeaves.push(leaf));

    for (const center of HD_BODYGRAPH_CENTERS) {
      const keepSet = new Set<Element>();
      const centerShapeElements = this.findElementsByAliases([`${center}-shape`, `${center}-shape-defined`]);
      centerShapeElements.forEach(el => this.collectDimLeavesIn(el, dimSelector, keepSet));
      this.hoverSourceCenterMarkersByCenter.set(center, centerShapeElements);

      const centerGates = HD_BODYGRAPH_GATES_BY_CENTER[center] ?? [];
      for (const gate of centerGates) {
        this.findElementsByAliases(getHdBodygraphGateSelectedAliases(gate))
          .forEach(el => this.collectDimLeavesIn(el, dimSelector, keepSet));
        (this.gateNumberTargetsByGate.get(gate) ?? [])
          .forEach(el => this.collectDimLeavesIn(el, dimSelector, keepSet));
      }
      this.hoverKeepLeavesByCenter.set(center, keepSet);
    }

    for (const gate of HD_BODYGRAPH_GATES) {
      const keepSet = new Set<Element>();
      const pieceElements = [
        ...this.findElementsByAliases(getHdBodygraphGateClickableChannelAliases(gate)),
        ...this.findElementsByAliases(this.getGateSegmentAliases(gate)),
      ];
      pieceElements.forEach(el => this.collectDimLeavesIn(el, dimSelector, keepSet));
      const pieceLeaves = new Set<Element>();
      pieceElements.forEach(el => this.collectDimLeavesIn(el, HD_GATE_PIECE_SHAPE_SELECTOR, pieceLeaves));
      this.hoverPieceLeavesByGate.set(gate, [...pieceLeaves]);
      const selectedElements = this.findElementsByAliases(getHdBodygraphGateSelectedAliases(gate));
      selectedElements.forEach(el => this.collectDimLeavesIn(el, dimSelector, keepSet));
      this.hoverKeepLeavesByGate.set(gate, keepSet);
      this.hoverSourceGateMarkersByGate.set(gate, selectedElements);
    }

    const clusterLeaves = new Set<Element>();
    this.findElementsByAliases([
      ...this.getSegmentVariantAliases(HD_BODYGRAPH_INTEGRATION_GATES.flatMap(getHdBodygraphGateLayerAliases)),
      ...getHdBodygraphCornerCapSegmentAliases(),
    ]).forEach(el => this.collectDimLeavesIn(el, HD_GATE_PIECE_SHAPE_SELECTOR, clusterLeaves));
    this.integrationClusterLeaves.push(...clusterLeaves);
  }

  private collectDimLeavesIn(root: Element, dimSelector: string, into: Set<Element>): void {
    if (root.matches(dimSelector)) {
      into.add(root);
    }
    root.querySelectorAll(dimSelector).forEach(leaf => into.add(leaf));
  }

  /**
   * Voegt een tijdelijke kanaal-overlay toe aan de render-state zonder de
   * gebruikers-`configuration` te muteren. Het opgegeven kanaal wordt visueel
   * "defined" gemaakt: beide gates en het kanaal zelf krijgen de overlay-tone
   * (`'highlight'` voor success-blauw, `'design'` voor error-rood), en de
   * verbonden centers worden aangevuld zodat ze meelichten. Hergebruikt het
   * bestaande renderpad voor `colorState`-gestuurde kanaal-rendering.
   */
  private applyTransientChannelHighlightOverlay(
    state: HdBodygraphRenderState,
    overlay: HdBodygraphTransientChannelHighlight | null,
  ): HdBodygraphRenderState {
    if (overlay == null) {
      return state;
    }

    const channel = state.channelStates[overlay.channelKey];
    if (channel == null) {
      return state;
    }

    const overlayColorState: HdBodygraphColorState = overlay.tone === 'success' ? 'highlight' : 'design';

    const overlaidGateStates = { ...state.gateStates };
    for (const gate of channel.gates) {
      const previous = overlaidGateStates[gate];
      if (previous == null) {
        continue;
      }
      overlaidGateStates[gate] = {
        ...previous,
        isActive: true,
        colorState: overlayColorState,
      };
    }

    const overlaidChannelStates = {
      ...state.channelStates,
      [channel.key]: { ...channel, isDefined: true },
    };

    const overlaidCenterStates = { ...state.centerStates };
    for (const center of channel.centers) {
      const previous = overlaidCenterStates[center];
      if (previous == null || previous.definedChannelKeys.includes(channel.key)) {
        continue;
      }
      overlaidCenterStates[center] = {
        ...previous,
        isDefined: true,
        definedChannelKeys: [...previous.definedChannelKeys, channel.key],
      };
    }

    return {
      gateStates: overlaidGateStates,
      channelStates: overlaidChannelStates,
      centerStates: overlaidCenterStates,
    };
  }

  /**
   * Voegt een tijdelijke poort-activatie-overlay toe aan de render-state voor
   * de meegegeven poorten. Elke poort wordt visueel "active" met de
   * highlight-kleur (blauw), zodat het kanaalstuk van die poort meelicht — een
   * korte feedback-flash op een correct poortenjacht-antwoord vóór de poort
   * terugvalt naar de neutrale "revealed"-state (enkel het cijfer zichtbaar).
   */
  private applyCorrectFlashGatesOverlay(
    state: HdBodygraphRenderState,
    correctFlashGates: ReadonlySet<number>,
  ): HdBodygraphRenderState {
    if (correctFlashGates.size === 0) {
      return state;
    }

    const overlaidGateStates = { ...state.gateStates };
    for (const gate of correctFlashGates) {
      const previous = overlaidGateStates[gate];
      if (previous == null) {
        continue;
      }
      overlaidGateStates[gate] = {
        ...previous,
        isActive: true,
        colorState: 'highlight',
      };
    }

    return {
      gateStates: overlaidGateStates,
      channelStates: state.channelStates,
      centerStates: state.centerStates,
    };
  }

  private emitToggledRevealedGate(gate: number): void {
    const current = this.revealedGates();
    const next = new Set(current);

    if (next.has(gate)) {
      next.delete(gate);
    } else {
      next.add(gate);
    }

    this.revealedGatesChange.emit(next);
  }

  private getGateSegmentAliases(gate: number): string[] {
    return this.getSegmentVariantAliases(getHdBodygraphGateLayerAliases(gate));
  }

  /** Breidt laag-aliassen uit met hun rode/zwarte sub-laag varianten. */
  private getSegmentVariantAliases(aliases: readonly string[]): string[] {
    return aliases.flatMap(alias => [alias, `${alias}r`, `${alias}b`]);
  }

  private cleanupListeners(): void {
    this.listenerCleanups.splice(0).forEach(cleanup => cleanup());
  }

  private applyRenderState(
    renderState: HdBodygraphRenderState,
    baseRenderState: HdBodygraphRenderState,
    transitOverlay: HdBodygraphTransitOverlay | null,
  ): void {
    const activeGates = new Set(
      HD_BODYGRAPH_GATES.filter(gate => renderState.gateStates[gate].isActive),
    );
    // Met een overlay leiden de bruglagen en corner-caps van het integratiegebied
    // hun kleur af in twee ruimtes:
    // - bronruimte: welke flows (basis/overlay) een stuk dragen
    //   (toHdOverlaySourceGateState);
    // - laagruimte: de P/D-kleur zoals elke poort getoond wordt. Twee-kleuren =
    //   de bronruimte zelf (basis/overlay ís daar de kleur); driekleuren = de
    //   getoonde P/D-kleur per poort.
    const sourceGateStates = transitOverlay == null
      ? null
      : this.resolveOverlaySourceGateStates(renderState, baseRenderState, transitOverlay);
    const layerGateStates = transitOverlay == null
      ? renderState.gateStates
      : transitOverlay.colorMode === 'two-color'
        ? sourceGateStates!
        : this.resolveThreeColorLayerGateStates(renderState, baseRenderState, transitOverlay);

    this.resetCompositeChannelVisibility();
    this.prepareTransitPaintServers(transitOverlay);

    const colors = this.colorConfig();

    for (const center of HD_BODYGRAPH_CENTERS) {
      const centerState = renderState.centerStates[center];
      const isDefined = centerState.isDefined;
      const definedAliases = [`${center}-shape-defined`];
      const baseAliases = [`${center}-shape`];

      this.setAliasesVisibility(definedAliases, isDefined);

      const definedElements = this.findElementsByAliases(definedAliases);
      const baseElements = this.findElementsByAliases(baseAliases);

      if (isDefined) {
        this.setElementsFill(definedElements, colors.centerDefined[center]);
        this.setElementsStroke(definedElements);
        this.setElementsStroke(baseElements);
      } else {
        const centerGates = HD_BODYGRAPH_GATES_BY_CENTER[center];
        const hasActiveGate = centerGates.some(gate => activeGates.has(gate));
        const fillColor = hasActiveGate ? colors.centerUndefined : colors.centerOpen;
        const strokeWidth = this.getCenterStrokeWidth(center, colors.centerBorderStyle);
        const strokeColor = colors.centerBorderStyle === 'none' ? undefined : colors.centerBorderColor;

        this.setElementsFill(baseElements, fillColor ?? undefined);
        this.setElementsStroke(baseElements, strokeColor, strokeWidth);
      }
    }

    for (const gate of HD_BODYGRAPH_GATES) {
      const gateState = renderState.gateStates[gate];
      const baseGateState = baseRenderState.gateStates[gate];
      const hasTransit = transitOverlay?.activations.some(activation => activation.gate === gate) ?? false;
      const transitVisual = transitOverlay == null
        ? null
        : resolveHdGateTransitVisual(baseGateState, hasTransit, transitOverlay.colorMode);
      const renderColorState = layerGateStates[gate].colorState;
      const candidateAliases = resolveHdBodygraphGateCandidateAliases(gate, activeGates);
      const baseAliases = resolveHdBodygraphGateBaseLayerAliases(gate, activeGates);
      const blackBaseAliases = resolveHdBodygraphBlackBaseLayerAliases(
        gate,
        activeGates,
        renderColorState,
      );
      const visibleAliases = resolveHdBodygraphGateLayerAliases(
        gate,
        activeGates,
        renderColorState,
      );
      const aliasColorStateOverrides = resolveHdBodygraphGateAliasColorStateOverrides(
        gate,
        layerGateStates,
        activeGates,
      );
      const aliasSourceOverrides = sourceGateStates == null
        ? {}
        : resolveHdBodygraphGateAliasColorStateOverrides(gate, sourceGateStates, activeGates);

      // De "selected"-laag (cirkel + binnenkijfer) is ALTIJD zichtbaar — ook
      // voor inactieve gates. Bij inactieve poorten krijgt de cirkel de fill
      // van het center (waardoor ze visueel "onzichtbaar" is) en wordt het
      // inner-cijfer (witte tekst bedoeld voor actieve weergave) verborgen
      // zodat het onderliggende zwarte basiscijfer leesbaar blijft. Doel:
      // de cirkel-shape vormt zo altijd de hover-area voor de poort, ook bij
      // inactieve gates — robuuster dan hoveren op een dun cijfer-glyph.
      this.setAliasesVisibility(getHdBodygraphGateSelectedAliases(gate), true);

      if (transitVisual != null) {
        this.applyTransitGateCircleColors(
          gate,
          renderState,
          baseRenderState,
          transitVisual,
          transitOverlay!.colorMode,
        );
      } else if (gateState.isActive) {
        this.applyActiveGateCircleColors(gate, renderState);
      } else {
        this.applyInactiveGateCircleColors(gate, renderState);
      }

      this.applyGateLayerState(
        candidateAliases,
        baseAliases,
        blackBaseAliases,
        visibleAliases,
        renderColorState,
        aliasColorStateOverrides,
      );

      if (transitVisual != null) {
        // Enkel de lagen die de ownership-logica ook echt inkleurt: candidate-
        // lagen buiten visibleAliases staan neutraal of verborgen (bv. de
        // 57-bruglagen c5710/c5720 bij een pure 34-57 definitie) en mogen niet
        // alsnog de overlay-kleur krijgen. Een laag met een afgeleide brugkleur
        // volgt de flows die erdoor lopen, niet de kleur van zijn eigenaarpoort.
        const colorMode = transitOverlay!.colorMode;
        for (const alias of visibleAliases) {
          const sourceState = aliasSourceOverrides[alias];
          const visual = sourceState == null
            ? transitVisual
            : resolveHdOverlayLayerVisual(sourceState, aliasColorStateOverrides[alias] ?? renderColorState, colorMode);
          this.applyTransitGateLayerColors([alias], visual, colorMode);
        }
      }
    }

    this.applyCornerCapLayerStates(layerGateStates, sourceGateStates, baseRenderState, activeGates, transitOverlay);
    this.applyShiftBrokenChannels(renderState);

    // De render heeft zonet de laag-ownership van het integratiegebied
    // (her)bepaald — synchroniseer de hover-keep-sets en een eventueel
    // actieve/bevroren hover met die nieuwe toewijzing.
    this.rebuildIntegrationHoverKeepCache(layerGateStates, activeGates);
    this.applyHoverFromService();
  }

  private resolveOverlaySourceGateStates(
    renderState: HdBodygraphRenderState,
    baseRenderState: HdBodygraphRenderState,
    transitOverlay: HdBodygraphTransitOverlay,
  ): Record<number, HdBodygraphGateState> {
    const transitGates = new Set(transitOverlay.activations.map(activation => activation.gate));
    const gateStates: Record<number, HdBodygraphGateState> = {};
    for (const gate of HD_BODYGRAPH_GATES) {
      gateStates[gate] = toHdOverlaySourceGateState(
        renderState.gateStates[gate],
        baseRenderState.gateStates[gate].isActive,
        transitGates.has(gate),
      );
    }
    return gateStates;
  }

  /**
   * Driekleuren: per poort de P/D-kleur zoals hij getoond wordt (basis houdt
   * zijn basiskleur, ook gearceerd; transit-only zijn transitkant). Zo lekt een
   * transitkant niet als P/D-kleur in een afgeleid stuk dat enkel basisflow draagt.
   */
  private resolveThreeColorLayerGateStates(
    renderState: HdBodygraphRenderState,
    baseRenderState: HdBodygraphRenderState,
    transitOverlay: HdBodygraphTransitOverlay,
  ): Record<number, HdBodygraphGateState> {
    const transitGates = new Set(transitOverlay.activations.map(activation => activation.gate));
    const gateStates: Record<number, HdBodygraphGateState> = {};
    for (const gate of HD_BODYGRAPH_GATES) {
      const gateState = renderState.gateStates[gate];
      const baseGateState = baseRenderState.gateStates[gate];
      const visual = resolveHdGateTransitVisual(baseGateState, transitGates.has(gate), 'three-color');
      const colorState = this.resolveTransitRenderColorState(gateState.colorState, baseGateState.colorState, visual);
      gateStates[gate] = colorState === gateState.colorState
        ? gateState
        : {
          ...gateState,
          colorState,
          hasPersonality: colorState === 'personality' || colorState === 'both',
          hasDesign: colorState === 'design' || colorState === 'both',
        };
    }
    return gateStates;
  }

  private resolveTransitRenderColorState(
    combinedColorState: HdBodygraphColorState,
    baseColorState: HdBodygraphColorState,
    visual: HdGateTransitVisual | null,
  ): HdBodygraphColorState {
    if (visual == null) {
      return combinedColorState;
    }
    if (visual.kind === 'split-base-transit' || visual.kind === 'hatched-both') {
      return 'both';
    }
    if (visual.kind === 'hatched-personality') {
      return 'personality';
    }
    if (visual.kind === 'hatched-design') {
      return 'design';
    }
    if (visual.kind === 'hatched-highlight') {
      return 'highlight';
    }
    return visual.kind === 'base' ? baseColorState : combinedColorState;
  }

  private applyTransitGateLayerColors(
    candidateAliases: readonly string[],
    visual: HdGateTransitVisual,
    colorMode: HdBodygraphTransitOverlay['colorMode'],
    channelAngleHint?: number,
  ): void {
    if (visual.kind === 'inactive' || (visual.kind === 'base' && colorMode === 'three-color')) {
      return;
    }

    const baseLayers = this.findElementsByAliases([...candidateAliases]);
    const designLayers = this.findElementsByAliases(candidateAliases.map(alias => `${alias}r`));
    const personalityLayers = this.findElementsByAliases(candidateAliases.map(alias => `${alias}b`));

    switch (visual.kind) {
      case 'base':
        this.setTransitElementsFill([...baseLayers, ...designLayers, ...personalityLayers], HD_JUST_NOW_TRANSIT_BASE_COLOR);
        return;
      case 'transit':
        this.setTransitElementsFill([...baseLayers, ...designLayers, ...personalityLayers], HD_JUST_NOW_TRANSIT_COLOR);
        return;
      case 'split-base-transit':
        this.setTransitElementsFill(baseLayers, HD_JUST_NOW_TRANSIT_BASE_COLOR);
        this.setTransitElementsFill(personalityLayers, HD_JUST_NOW_TRANSIT_BASE_COLOR);
        this.setTransitElementsFill(designLayers, HD_JUST_NOW_TRANSIT_COLOR);
        return;
      case 'hatched-personality':
        this.setTransitElementsPattern(
          [...baseLayers, ...personalityLayers],
          this.colorConfig().channelPersonality,
          channelAngleHint,
        );
        return;
      case 'hatched-design':
        this.setTransitElementsPattern(
          [...baseLayers, ...designLayers],
          this.colorConfig().channelDesign,
          channelAngleHint,
        );
        return;
      case 'hatched-both':
        this.setTransitElementsPattern(
          [...baseLayers, ...personalityLayers],
          this.colorConfig().channelPersonality,
          channelAngleHint,
        );
        this.setTransitElementsPattern(designLayers, this.colorConfig().channelDesign, channelAngleHint);
        return;
      case 'hatched-highlight':
        this.setTransitElementsPattern(
          [...baseLayers, ...personalityLayers],
          this.colorConfig().channelHighlight,
          channelAngleHint,
        );
        return;
    }
  }

  private applyTransitGateCircleColors(
    gate: number,
    combinedRenderState: HdBodygraphRenderState,
    baseRenderState: HdBodygraphRenderState,
    visual: HdGateTransitVisual,
    colorMode: HdBodygraphTransitOverlay['colorMode'],
  ): void {
    if (visual.kind === 'inactive') {
      this.applyInactiveGateCircleColors(gate, combinedRenderState);
      return;
    }
    if (visual.kind === 'base') {
      if (colorMode === 'two-color') {
        this.applySolidGateCircleColors(gate, HD_JUST_NOW_TRANSIT_BASE_COLOR, 'rgb(255,255,255)');
      } else {
        this.applyActiveGateCircleColors(gate, combinedRenderState, true);
      }
      return;
    }
    if (visual.kind === 'transit') {
      this.applySolidGateCircleColors(gate, HD_JUST_NOW_TRANSIT_COLOR, HD_JUST_NOW_TRANSIT_TEXT_COLOR);
      return;
    }
    if (visual.kind === 'split-base-transit') {
      this.applySplitGateCircleColors(
        gate,
        HD_JUST_NOW_TRANSIT_BASE_COLOR,
        HD_JUST_NOW_TRANSIT_COLOR,
        'rgb(255,255,255)',
        HD_JUST_NOW_TRANSIT_TEXT_COLOR,
      );
      return;
    }

    this.applyActiveGateCircleColors(
      gate,
      baseRenderState.gateStates[gate].isActive ? combinedRenderState : baseRenderState,
      true,
    );
    const selectedElements = this.findElementsByAliases(getHdBodygraphGateSelectedAliases(gate));
    selectedElements.forEach(element => {
      element.querySelectorAll('ellipse, circle').forEach(shape => {
        if (shape instanceof SVGElement) {
          shape.style.stroke = HD_JUST_NOW_TRANSIT_COLOR;
          shape.style.strokeWidth = '3px';
        }
      });
    });
  }

  private applySolidGateCircleColors(
    gate: number,
    fill: string,
    textColor: string,
    textStroke?: string,
  ): void {
    const selectedElements = this.findElementsByAliases(getHdBodygraphGateSelectedAliases(gate));
    selectedElements.forEach(element => {
      element.querySelectorAll('ellipse, circle').forEach(shape => {
        if (shape instanceof SVGElement) {
          shape.style.fill = fill;
          shape.style.stroke = fill;
          shape.style.removeProperty('stroke-width');
        }
      });
      element.querySelectorAll('text, tspan').forEach(textNode => {
        if (!(textNode instanceof SVGElement)) {
          return;
        }
        textNode.style.fill = textColor;
        textNode.style.display = '';
        if (textStroke) {
          textNode.style.stroke = textStroke;
          textNode.style.strokeWidth = '0.75px';
          textNode.style.paintOrder = 'stroke fill';
        } else {
          textNode.style.removeProperty('stroke');
          textNode.style.removeProperty('stroke-width');
          textNode.style.removeProperty('paint-order');
        }
      });
    });
  }

  /**
   * Gesplitste poortcirkel (links `leftFill`, rechts `rightFill`): de cirkel
   * zelf krijgt de rechterkleur en de geclipte kloon uit
   * {@link injectSplitGateCircleHalves} de linkerkleur. In alle andere
   * kleurpaden volgt de kloon gewoon de cirkel (zelfde fill/stroke), en is hij
   * dus onzichtbaar.
   */
  private applySplitGateCircleColors(
    gate: number,
    leftFill: string,
    rightFill: string,
    textColor: string,
    textStroke?: string,
  ): void {
    this.applySolidGateCircleColors(gate, rightFill, textColor, textStroke);
    this.splitGateCircleHalvesByGate.get(gate)?.forEach(half => {
      half.style.fill = leftFill;
      half.style.stroke = leftFill;
    });
  }

  private prepareTransitPaintServers(transitOverlay: HdBodygraphTransitOverlay | null): void {
    const svg = this.svgRoot;
    if (svg == null) {
      return;
    }
    this.transitPaintedShapes.forEach(shape => this.restoreOriginalFill(shape));
    this.transitPaintedShapes.clear();
    svg.querySelectorAll<SVGElement>('path, rect, ellipse, polygon, circle').forEach(shape => {
      if (shape.style.fill.includes('hd-just-now-transit-')) {
        this.restoreOriginalFill(shape);
      }
    });
    svg.querySelector('#hd-just-now-transit-defs')?.remove();
    this.transitHatchPatternIds.clear();
    this.transitHatchPatternSequence = 0;
    if (transitOverlay == null) {
      return;
    }

    const defs = document.createElementNS(SVG_NAMESPACE, 'defs');
    defs.id = 'hd-just-now-transit-defs';
    svg.insertBefore(defs, svg.firstChild);
  }

  private createTransitHatchPattern(
    id: string,
    backgroundColor: string,
    channelAngle: number,
    patternMatrix: DOMMatrix,
  ): SVGPatternElement {
    const pattern = document.createElementNS(SVG_NAMESPACE, 'pattern');
    pattern.id = id;
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    pattern.setAttribute('width', `${TRANSIT_HATCH_PERIOD}`);
    pattern.setAttribute('height', `${TRANSIT_HATCH_PERIOD}`);
    pattern.setAttribute('patternTransform', this.serializeSvgMatrix(patternMatrix));
    pattern.setAttribute('data-channel-angle', `${channelAngle}`);

    const background = document.createElementNS(SVG_NAMESPACE, 'rect');
    background.setAttribute('width', `${TRANSIT_HATCH_PERIOD}`);
    background.setAttribute('height', `${TRANSIT_HATCH_PERIOD}`);
    background.setAttribute('fill', backgroundColor);

    const stripe = document.createElementNS(SVG_NAMESPACE, 'rect');
    stripe.classList.add('hd-just-now-transit-rung');
    stripe.setAttribute('width', `${TRANSIT_HATCH_RUNG_WIDTH}`);
    stripe.setAttribute('height', `${TRANSIT_HATCH_PERIOD}`);
    stripe.setAttribute('fill', HD_JUST_NOW_TRANSIT_COLOR);

    pattern.append(background, stripe);
    return pattern;
  }

  private getOrCreateTransitHatchPattern(
    backgroundColor: string,
    shape: SVGGraphicsElement,
    channelAngle: number,
  ): string {
    const normalizedAngle = this.normalizeTransitChannelAngle(channelAngle);
    const patternMatrix = this.resolveTransitPatternMatrix(shape, normalizedAngle);
    const key = `${backgroundColor}:${this.serializeSvgMatrix(patternMatrix)}`;
    const existingId = this.transitHatchPatternIds.get(key);
    if (existingId != null) {
      return existingId;
    }
    const defs = this.svgRoot?.querySelector('#hd-just-now-transit-defs');
    if (!(defs instanceof SVGDefsElement)) {
      return '';
    }
    const id = `hd-just-now-transit-hatch-${this.transitHatchPatternSequence++}`;
    defs.append(this.createTransitHatchPattern(id, backgroundColor, normalizedAngle, patternMatrix));
    this.transitHatchPatternIds.set(key, id);
    return id;
  }

  private normalizeTransitChannelAngle(angle: number): number {
    const normalized = ((angle % 180) + 180) % 180;
    return Math.round(normalized);
  }

  private resolveTransitPatternMatrix(shape: SVGGraphicsElement, channelAngle: number): DOMMatrix {
    const shapeCtm = shape.getCTM();
    const radians = channelAngle * Math.PI / 180;
    const rootRotation = new DOMMatrix([
      Math.cos(radians),
      Math.sin(radians),
      -Math.sin(radians),
      Math.cos(radians),
      0,
      0,
    ]);
    if (shapeCtm == null) {
      return rootRotation;
    }
    try {
      const localToRoot = new DOMMatrix([
        shapeCtm.a, shapeCtm.b, shapeCtm.c,
        shapeCtm.d, shapeCtm.e, shapeCtm.f,
      ]);
      return localToRoot.inverse().multiply(rootRotation);
    } catch {
      return rootRotation;
    }
  }

  private serializeSvgMatrix(matrix: DOMMatrix): string {
    const values = [matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f]
      .map(value => Math.abs(value) < 0.000001 ? 0 : Number(value.toFixed(6)));
    return `matrix(${values.join(' ')})`;
  }

  private paintServerUrl(id: string): string {
    return `url(#${id})`;
  }

  /**
   * Herbouwt de hover-keep-sets van de integratiepoorten met exact dezelfde
   * ownership-logica als de render-pass. Per poort: de huidige kandidaat-
   * lagen ({@link resolveHdBodygraphGateCandidateAliases} — bevat bv. voor
   * gate 20 het brugstuk `c5720` zodra 20+34 actief zijn) plus de momenteel
   * zichtbare corner-caps van het joint-gebied
   * ({@link resolveHdBodygraphCornerCapLayerStates}) waarvan de poort eigenaar
   * is ({@link resolveHdBodygraphCornerCapOwnerGate}). Draait per render-pass
   * (niet per hover-event), zodat het hover-pad lookup-only blijft.
   *
   * BELANGRIJK: enkel de eigen caps, net als de poortstuk-outline. Voorheen
   * hield elke actieve integratiepoort álle zichtbare caps helder; een hover op
   * 10 in de 10-20-34-trio liet zo de cap van 34 los in het gedimde gebied
   * staan (Svetlana's solar return 2026). Specs: "geen losse caps" in
   * hd-bodygraph-chart.component.spec.ts.
   */
  private rebuildIntegrationHoverKeepCache(
    gateStates: Record<number, HdBodygraphGateState>,
    activeGates: ReadonlySet<number>,
  ): void {
    this.hoverIntegrationKeepLeavesByGate.clear();
    this.outlineIntegrationLeavesByGate.clear();

    const visibleCapAliases = resolveHdBodygraphCornerCapLayerStates(gateStates, activeGates)
      .map(cornerCapState => cornerCapState.alias);

    for (const gate of HD_BODYGRAPH_INTEGRATION_GATES) {
      const keepSet = new Set<Element>();
      const candidateAliases = resolveHdBodygraphGateCandidateAliases(gate, activeGates);
      const ownedVisibleCapAliases = visibleCapAliases
        .filter(alias => resolveHdBodygraphCornerCapOwnerGate(alias, activeGates) === gate);
      this.findElementsByAliases(this.getSegmentVariantAliases([...candidateAliases, ...ownedVisibleCapAliases]))
        .forEach(el => this.collectDimLeavesIn(el, HOVER_DIM_LEAF_SELECTOR, keepSet));

      this.hoverIntegrationKeepLeavesByGate.set(gate, keepSet);

      const outlineLeaves = new Set<Element>();
      const ownedCapAliases = getHdBodygraphCornerCapAliases()
        .filter(alias => resolveHdBodygraphCornerCapOwnerGate(alias, activeGates) === gate);
      this.findElementsByAliases(this.getSegmentVariantAliases([...candidateAliases, ...ownedCapAliases]))
        .forEach(el => this.collectDimLeavesIn(el, HD_GATE_PIECE_SHAPE_SELECTOR, outlineLeaves));
      this.outlineIntegrationLeavesByGate.set(gate, [...outlineLeaves]);
    }
  }

  private applyActiveGateCircleColors(
    gate: number,
    renderState: HdBodygraphRenderState,
    forceSourceColors = false,
  ): void {
    const center = HD_BODYGRAPH_GATE_TO_CENTER[gate];

    if (!center) {
      return;
    }

    const gateState = renderState.gateStates[gate];
    if (forceSourceColors || this.colorConfig().activeGateColorPreset === 'source') {
      if (gateState.colorState === 'both') {
        this.applySplitGateCircleColors(
          gate,
          this.colorConfig().channelDesign,
          this.colorConfig().channelPersonality,
          'rgb(255,255,255)',
        );
        return;
      }
      const sourceFill = gateState.colorState === 'design'
        ? this.colorConfig().channelDesign
        : gateState.colorState === 'highlight'
          ? this.colorConfig().channelHighlight
          : this.colorConfig().channelPersonality;
      this.applySolidGateCircleColors(gate, sourceFill, 'rgb(255,255,255)');
      return;
    }

    const isCenterDefined = renderState.centerStates[center].isDefined;
    const { circleFill, textColor } = resolveHdBodygraphActiveGateCircleColors(
      this.colorConfig(),
      center,
      isCenterDefined,
    );

    const selectedElements = this.findElementsByAliases(getHdBodygraphGateSelectedAliases(gate));
    /**
     * Stroke krijgt bewust dezelfde waarde als fill (i.p.v. lege string die
     * terugvalt op een SVG-default of geërfde waarde). Tijdens de 120ms
     * `is-hover-dim` transitie interpoleert de stroke nu synchroon met de
     * fill van actieve-kleur naar `#e6e6e6` — zonder deze sync zou de stroke
     * van een andere oorsprongswaarde interpoleren en op warm-gekleurde
     * actieve cirkels een "witte ring"-flits veroorzaken (zelfde mechanisme
     * als bij {@link applyInactiveGateCircleColors}).
     */
    selectedElements.forEach(element => {
      element.querySelectorAll('ellipse, circle').forEach(shape => {
        if (shape instanceof SVGElement) {
          shape.style.fill = circleFill;
          shape.style.stroke = circleFill;
          shape.style.removeProperty('stroke-width');
        }
      });

      element.querySelectorAll('text, tspan').forEach(textNode => {
        if (textNode instanceof SVGElement) {
          textNode.style.fill = textColor;
          textNode.style.display = '';
          textNode.style.removeProperty('stroke');
          textNode.style.removeProperty('stroke-width');
          textNode.style.removeProperty('paint-order');
        }
      });
    });
  }

  /**
   * Inactieve gates hergebruiken dezelfde "selected"-laag (cirkel + cijfer)
   * als actieve gates, maar gevuld met de center-kleur (waardoor de cirkel
   * visueel verdwijnt) en het binnenkijfer in dark text — zo lijkt het
   * alsof enkel het cijfer op de center-achtergrond staat, terwijl de
   * cirkel-shape de hover-area voor de poort blijft. Het onderliggende
   * "base"-cijferlaag is hierdoor niet langer nodig om het inactieve
   * cijfer te tonen; het zou er bovendien onder de cirkel gewoon achter
   * verdwijnen.
   */
  private applyInactiveGateCircleColors(gate: number, renderState: HdBodygraphRenderState): void {
    const center = HD_BODYGRAPH_GATE_TO_CENTER[gate];
    if (!center) return;

    const colors = this.colorConfig();
    const centerState = renderState.centerStates[center];
    let centerFill: string | undefined;
    if (centerState.isDefined) {
      centerFill = colors.centerDefined[center];
    } else {
      const centerGates = HD_BODYGRAPH_GATES_BY_CENTER[center];
      const hasActiveGate = centerGates.some(g => renderState.gateStates[g].isActive);
      centerFill = (hasActiveGate ? colors.centerUndefined : colors.centerOpen) ?? undefined;
    }

    const selectedElements = this.findElementsByAliases(getHdBodygraphGateSelectedAliases(gate));
    /**
     * Stroke krijgt bewust dezelfde waarde als fill (i.p.v. 'none'). In steady
     * state is dat visueel identiek — de stroke smelt weg tegen de center-fill
     * — maar tijdens de 120ms `is-hover-dim` fill+stroke transitie naar
     * `#e6e6e6` interpoleert de stroke nu synchroon met de fill. Met
     * `stroke: 'none'` interpreteert de browser dit als `transparent` en
     * fade-int de grijze CSS-stroke onafhankelijk in, wat samen met de
     * fill-interpolatie (die voor rood/bruin/tan-centers door near-white
     * passeert) een zichtbare "witte ring"-flits rond elke gate-cirkel
     * veroorzaakt op sacraal/root/solarplexus/keel.
     */
    const inactiveStroke = centerFill ?? 'none';
    // Licht cijfer op donkere centers (navy/donkerrood van Whale);
    // alle klassieke paletten houden donkere tekst.
    const inactiveTextColor = resolveHdBodygraphCenterTextColor(centerFill);
    selectedElements.forEach(element => {
      element.querySelectorAll('ellipse, circle').forEach(shape => {
        if (shape instanceof SVGElement) {
          shape.style.fill = centerFill ?? '';
          shape.style.stroke = inactiveStroke;
          shape.style.removeProperty('stroke-width');
        }
      });

      element.querySelectorAll('text, tspan').forEach(textNode => {
        if (textNode instanceof SVGElement) {
          textNode.style.fill = inactiveTextColor;
          textNode.style.display = '';
          textNode.style.removeProperty('stroke');
          textNode.style.removeProperty('stroke-width');
          textNode.style.removeProperty('paint-order');
        }
      });
    });
  }

  private resetCompositeChannelVisibility(): void {
    // Sommige scenario's verbergen een volledig kanaalgroup om de juiste bridge-compositie
    // te bekomen. Voor elke nieuwe render resetten we die visibility eerst, zodat een vorige
    // hide-state niet blijft hangen wanneer de chart terug neutraal wordt of wanneer bridge
    // ownership verschuift door een andere gatecombinatie.
    this.setAliasesVisibility(['channel5710', 'channel5734'], true);
    this.resetCornerCapLayerStates();
  }

  private resetCornerCapLayerStates(): void {
    getHdBodygraphCornerCapAliases().forEach(alias => {
      this.applyGateLayerState([alias], [], [], [], 'inactive', {});
    });
  }

  private applyCornerCapLayerStates(
    gateStates: Record<number, HdBodygraphGateState>,
    sourceGateStates: Record<number, HdBodygraphGateState> | null,
    baseRenderState: HdBodygraphRenderState,
    activeGates: ReadonlySet<number>,
    transitOverlay: HdBodygraphTransitOverlay | null,
  ): void {
    const sourceCapStates = sourceGateStates == null
      ? []
      : resolveHdBodygraphCornerCapLayerStates(sourceGateStates, activeGates);
    resolveHdBodygraphCornerCapLayerStates(gateStates, activeGates).forEach(cornerCapState => {
      const shouldRenderAsNeutralBase = cornerCapState.colorState === 'inactive';

      this.applyGateLayerState(
        [cornerCapState.alias],
        shouldRenderAsNeutralBase ? [cornerCapState.alias] : [],
        [],
        shouldRenderAsNeutralBase ? [] : [cornerCapState.alias],
        cornerCapState.colorState,
        {},
      );

      // Een neutrale cap is een cover (bv. over het c34r-hoekuitsteeksel) en
      // moet neutraal blijven — ook wanneer de owner-poort overlay-activaties
      // draagt, anders komt het afgedekte uitsteeksel gekleurd terug.
      if (shouldRenderAsNeutralBase || transitOverlay == null) {
        return;
      }
      const ownerGate = resolveHdBodygraphCornerCapOwnerGate(cornerCapState.alias, activeGates);
      const patternAngle = ownerGate == null ? undefined : this.resolveTransitPatternAngleForGate(ownerGate, activeGates);
      // Een cap verlengt een flow tot in de hoek: hij volgt de flows van zijn
      // hoek in de bronruimte (basis, overlay of beide), niet de kleur van zijn
      // eigenaarpoort — anders kreeg bv. de lower cap van een basisbrug 20-34
      // de transitkleur van 34. In de twee-kleurenmodus is de bronruimte de
      // laagruimte zelf, dus is de bron-cap altijd dezelfde cap.
      const sourceCap = sourceCapStates.find(state => isSameHdBodygraphCornerCapCorner(state.alias, cornerCapState.alias));
      if (sourceCap != null && sourceCap.colorState !== 'inactive') {
        this.applyTransitGateLayerColors(
          [cornerCapState.alias],
          resolveHdOverlayLayerVisual(sourceCap.colorState, cornerCapState.colorState, transitOverlay.colorMode),
          transitOverlay.colorMode,
          patternAngle,
        );
        return;
      }
      if (ownerGate == null) {
        return;
      }
      // Terugval (driekleuren, geen cap op deze hoek in de bronruimte): de
      // overlay-vertaling van de owner-poort ('base' is een no-op, de cap houdt
      // zijn P/D-kleur).
      const hasTransit = transitOverlay.activations.some(activation => activation.gate === ownerGate);
      const visual = resolveHdGateTransitVisual(
        baseRenderState.gateStates[ownerGate],
        hasTransit,
        transitOverlay.colorMode,
      );
      this.applyTransitGateLayerColors([cornerCapState.alias], visual, transitOverlay.colorMode, patternAngle);
    });
  }

  private resolveTransitPatternAngleForGate(gate: number, activeGates: ReadonlySet<number>): number | undefined {
    const aliases = resolveHdBodygraphGateCandidateAliases(gate, activeGates);
    for (const group of this.findElementsByAliases(this.getSegmentVariantAliases(aliases))) {
      const shape = group.querySelector<SVGElement>('path, rect, polygon, polyline, line');
      if (shape != null && shape.style.display !== 'none') {
        return this.resolveTransitChannelAngle(shape);
      }
    }
    return undefined;
  }

  private applyGateLayerState(
    candidateAliases: string[],
    baseAliases: string[],
    blackBaseAliases: string[],
    visibleAliases: string[],
    colorState: HdBodygraphColorState,
    aliasColorStateOverrides: Partial<Record<string, HdBodygraphColorState>>,
  ): void {
    const baseAliasSet = new Set(baseAliases);
    const blackBaseAliasSet = new Set(blackBaseAliases);
    const visibleAliasSet = new Set(visibleAliases);

    candidateAliases.forEach(alias => {
      const effectiveColorState = aliasColorStateOverrides[alias] ?? colorState;
      const baseLayers = this.findElementsByAliases([alias]);
      const redLayers = this.findElementsByAliases([`${alias}r`]);
      const blackLayers = this.findElementsByAliases([`${alias}b`]);
      const hasBaseLayers = baseLayers.length > 0;
      const shouldShowBaseAlias = baseAliasSet.has(alias);
      const shouldForceBlackBase = blackBaseAliasSet.has(alias);
      const shouldShowColorAlias = visibleAliasSet.has(alias);

      if (!shouldShowBaseAlias && !shouldShowColorAlias) {
        this.setElementsVisibility(baseLayers, false);
        this.setElementsVisibility(redLayers, false);
        this.setElementsVisibility(blackLayers, false);
        this.setElementsFill(baseLayers);
        return;
      }

      this.applyPaletteToSourceLayers([...redLayers, ...blackLayers]);

      switch (effectiveColorState) {
        case 'inactive':
          this.setElementsVisibility(baseLayers, shouldShowBaseAlias);
          this.setElementsVisibility(redLayers, false);
          this.setElementsVisibility(blackLayers, false);
          this.setElementsFill(baseLayers);
          break;
        case 'personality':
          this.setElementsVisibility(baseLayers, shouldShowBaseAlias || (shouldShowColorAlias && hasBaseLayers));
          this.setElementsVisibility(redLayers, false);
          this.setElementsVisibility(blackLayers, shouldShowColorAlias && !hasBaseLayers);

          if ((shouldShowColorAlias || shouldForceBlackBase) && hasBaseLayers) {
            this.setElementsFill(baseLayers, this.colorConfig().channelPersonality);
          } else {
            this.setElementsFill(baseLayers);
          }

          break;
        case 'design':
          this.setElementsVisibility(baseLayers, shouldShowBaseAlias || (shouldShowColorAlias && hasBaseLayers));
          this.setElementsVisibility(redLayers, shouldShowColorAlias && !hasBaseLayers);
          this.setElementsVisibility(blackLayers, false);

          if ((shouldShowColorAlias || shouldForceBlackBase) && hasBaseLayers) {
            this.setElementsFill(
              baseLayers,
              shouldForceBlackBase ? this.colorConfig().channelPersonality : this.colorConfig().channelDesign,
            );
          } else {
            this.setElementsFill(baseLayers);
          }

          break;
        case 'both':
          this.setElementsVisibility(baseLayers, shouldShowBaseAlias && (!shouldShowColorAlias || shouldForceBlackBase));
          this.setElementsVisibility(redLayers, shouldShowColorAlias);
          this.setElementsVisibility(blackLayers, shouldShowColorAlias);

          if (shouldForceBlackBase) {
            this.setElementsFill(baseLayers, this.colorConfig().channelPersonality);
          } else if (!shouldShowColorAlias) {
            this.setElementsFill(baseLayers);
          }

          break;
        case 'highlight':
          this.setElementsVisibility(baseLayers, shouldShowBaseAlias || (shouldShowColorAlias && hasBaseLayers));
          this.setElementsVisibility(redLayers, false);
          this.setElementsVisibility(blackLayers, shouldShowColorAlias && !hasBaseLayers);

          if ((shouldShowColorAlias || shouldForceBlackBase) && hasBaseLayers) {
            this.setElementsFill(baseLayers, this.colorConfig().channelHighlight);
          } else if (shouldShowColorAlias && !hasBaseLayers) {
            this.setElementsFill(blackLayers, this.colorConfig().channelHighlight);
          } else {
            this.setElementsFill(baseLayers);
          }

          break;
      }
    });
  }

  /**
   * BELANGRIJK: de r/b-sublagen (half/half-stukken bij 'both' en stukken zonder
   * basislaag) dragen in de SVG de klassieke bronkleuren. Zonder deze herkleuring
   * toonden ze dat rauwe rood/zwart naast effen stukken in de paletkleur (bij
   * Warm Dusk een ander rood in half/half-stukken). Er wordt per shape op de
   * bronkleur gemapt en niet per laagnaam, omdat c40r/c40b in de SVG omgewisselde
   * kleuren hebben en hun bestaande uitzicht moeten houden. Specs: "half/half-
   * kanaalstukken volgen het palet" in hd-bodygraph-chart.component.spec.ts.
   */
  private applyPaletteToSourceLayers(elements: readonly SVGElement[]): void {
    const colors = this.colorConfig();
    elements.forEach(element => {
      element.querySelectorAll('path, rect, ellipse, polygon, circle').forEach(shape => {
        if (!(shape instanceof SVGElement)) {
          return;
        }

        const sourceFill = this.originalFillValues.get(shape)?.replace(/\s+/g, '');
        if (sourceFill === HD_BODYGRAPH_SVG_DESIGN_SOURCE_FILL) {
          shape.style.fill = colors.channelDesign;
          return;
        }
        if (sourceFill === HD_BODYGRAPH_SVG_PERSONALITY_SOURCE_FILL) {
          shape.style.fill = colors.channelPersonality;
          return;
        }

        this.restoreOriginalFill(shape);
      });
    });
  }

  private setAliasesVisibility(aliases: string[], visible: boolean): void {
    this.findElementsByAliases(aliases).forEach(element => {
      element.style.display = visible ? '' : 'none';
    });
  }

  private setElementsVisibility(elements: SVGElement[], visible: boolean): void {
    elements.forEach(element => {
      element.style.display = visible ? '' : 'none';
    });
  }

  private setElementsFill(elements: SVGElement[], fillColor?: string): void {
    elements.forEach(element => {
      element.querySelectorAll('path, rect, ellipse, polygon, circle').forEach(shape => {
        if (!(shape instanceof SVGElement)) {
          return;
        }

        if (fillColor) {
          shape.style.fill = fillColor;
          return;
        }

        this.restoreOriginalFill(shape);
      });
    });
  }

  private setTransitElementsFill(elements: SVGElement[], fillColor: string): void {
    elements.forEach(element => {
      element.querySelectorAll('path, rect, ellipse, polygon, circle').forEach(shape => {
        if (!(shape instanceof SVGElement)) {
          return;
        }
        this.transitPaintedShapes.add(shape);
        shape.style.fill = fillColor;
      });
    });
  }

  private setTransitElementsPattern(
    elements: readonly SVGElement[],
    backgroundColor: string,
    channelAngleHint?: number,
  ): void {
    elements.forEach(element => {
      element.querySelectorAll<SVGElement>('path, rect, ellipse, polygon, circle').forEach(shape => {
        const channelAngle = channelAngleHint ?? this.resolveTransitChannelAngle(shape);
        const patternId = this.getOrCreateTransitHatchPattern(
          backgroundColor,
          shape as SVGGraphicsElement,
          channelAngle,
        );
        if (!patternId) {
          return;
        }
        this.transitPaintedShapes.add(shape);
        shape.style.fill = this.paintServerUrl(patternId);
      });
    });
  }

  private resolveTransitChannelAngle(shape: SVGElement): number {
    if (shape instanceof SVGRectElement) {
      return this.resolveTransitChannelAngleFromBounds(shape);
    }
    const geometry = shape as SVGGeometryElement;
    if (typeof geometry.getTotalLength !== 'function' || typeof geometry.getPointAtLength !== 'function') {
      return this.resolveTransitChannelAngleFromBounds(shape);
    }
    try {
      const totalLength = geometry.getTotalLength();
      if (!Number.isFinite(totalLength) || totalLength <= 0) {
        return this.resolveTransitChannelAngleFromBounds(shape);
      }
      const matrix = (shape as SVGGraphicsElement).getCTM();
      const points = Array.from({ length: 24 }, (_, index) => {
        const point = geometry.getPointAtLength((totalLength * index) / 24);
        return matrix == null
          ? { x: point.x, y: point.y }
          : {
            x: matrix.a * point.x + matrix.c * point.y + matrix.e,
            y: matrix.b * point.x + matrix.d * point.y + matrix.f,
          };
      });
      return this.resolveDominantAxisAngle(points);
    } catch {
      return this.resolveTransitChannelAngleFromBounds(shape);
    }
  }

  private resolveTransitChannelAngleFromBounds(shape: SVGElement): number {
    try {
      const bounds = (shape as SVGGraphicsElement).getBBox();
      const matrix = (shape as SVGGraphicsElement).getCTM();
      const localAngle = bounds.width >= bounds.height ? 0 : 90;
      if (matrix == null) {
        return localAngle;
      }
      const radians = localAngle * Math.PI / 180;
      const x = matrix.a * Math.cos(radians) + matrix.c * Math.sin(radians);
      const y = matrix.b * Math.cos(radians) + matrix.d * Math.sin(radians);
      return Math.atan2(y, x) * 180 / Math.PI;
    } catch {
      return 0;
    }
  }

  private resolveDominantAxisAngle(points: readonly { x: number; y: number }[]): number {
    const centerX = points.reduce((sum, point) => sum + point.x, 0) / points.length;
    const centerY = points.reduce((sum, point) => sum + point.y, 0) / points.length;
    let covarianceX = 0;
    let covarianceY = 0;
    let covarianceXY = 0;
    for (const point of points) {
      const x = point.x - centerX;
      const y = point.y - centerY;
      covarianceX += x * x;
      covarianceY += y * y;
      covarianceXY += x * y;
    }
    return 0.5 * Math.atan2(2 * covarianceXY, covarianceX - covarianceY) * 180 / Math.PI;
  }

  private setElementsStroke(elements: SVGElement[], strokeColor?: string, strokeWidth: string = '2px'): void {
    elements.forEach(element => {
      element.querySelectorAll('path, rect, ellipse, polygon, circle').forEach(shape => {
        if (!(shape instanceof SVGElement)) {
          return;
        }

        if (strokeColor) {
          shape.style.stroke = strokeColor;
          shape.style.strokeWidth = strokeWidth;
          return;
        }

        shape.style.removeProperty('stroke');
        shape.style.removeProperty('stroke-width');
      });
    });
  }

  private getCenterStrokeWidth(center: HdBodygraphCenter, style: HdBodygraphCenterBorderStyle): string {
    if (style === 'none') {
      return '0px';
    }

    const isThreeCorner = isHdBodygraphThreeCornerCenter(center);

    if (style === 'thin') {
      return isThreeCorner ? '1px' : '2px';
    }

    if (style === 'medium') {
      return isThreeCorner ? '2px' : '3px';
    }

    if (style === 'thick') {
      return isThreeCorner ? '3px' : '5px';
    }

    return '2px';
  }

  private cacheOriginalFill(element: SVGElement): void {
    if (this.originalFillValues.has(element)) {
      return;
    }

    const originalFill = element.style.fill || element.getAttribute('fill');
    this.originalFillValues.set(element, originalFill && originalFill.length > 0 ? originalFill : null);
  }

  private restoreOriginalFill(element: SVGElement): void {
    const originalFill = this.originalFillValues.get(element);

    if (originalFill) {
      element.style.fill = originalFill;
      return;
    }

    element.style.removeProperty('fill');
  }

  private findElementsByAliases(aliases: string[]): SVGElement[] {
    const elements = new Set<SVGElement>();

    aliases.forEach(alias => {
      const exactMatches = this.indexedElementsById.get(alias);
      const fallbackMatches = exactMatches?.length ? [] : (this.indexedElementsByAlias.get(alias) ?? []);

      [...(exactMatches ?? []), ...fallbackMatches].forEach(element => elements.add(element));
    });

    return [...elements];
  }
}
